import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as SmartToyIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Search as SearchIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  MedicalServices as MedicalServicesIcon,
  LocalHospital as HospitalIcon
} from '@mui/icons-material';
import axios from '../../config/axios';
import { io, Socket } from 'socket.io-client';
import { getApiUrl, getWebSocketUrl } from '../../config/environment';
import { logger } from '../../utils/logger';
import BotToggleButton from '../../components/BotToggleButton';
import QuickMessagesButton from '../../components/QuickMessagesButton';
import AttendantAssignment from '../../components/AttendantAssignment';
import HealthcarePanel from '../../components/HealthcarePanel';
import ChatSupportEventsBanner from '../../components/ChatSupportEventsBanner';
import LeadContactTags, { resolveConversationContactId } from '../../components/Inbox/LeadContactTags';
import InboxConversationTagFilter, {
  inboxTagFilterQuery
} from '../../components/Inbox/InboxConversationTagFilter';
import ConversationListContactTags from '../../components/Inbox/ConversationListContactTags';

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  source?: string;
  custom_fields?: {
    whatsapp_id?: string;
    username?: string;
  };
}

interface Conversation {
  id: string;
  contact_id: string;
  channel_id: string;
  external_id?: string;
  last_message?: string;
  last_message_at?: string;
  unread_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  contact?: Contact;
  channel?: {
    id: string;
    name: string;
    type: string;
    status: string;
  };
  contact_tags?: Array<{ id: string; name: string; color: string | null }>;
}

interface Message {
  id: string;
  conversation_id: string;
  external_id?: string;
  content: string;
  direction: 'inbound' | 'outbound';
  sent_at: string;
  status: string;
  content_type?: string;
  metadata?: {
    messageId?: string;
    zaapId?: string;
    external_id?: string;
    [key: string]: any;
  };
  attachments?: Array<{
    type: string;
    url: string;
    file_id?: string;
    width?: number;
    height?: number;
    file_name?: string;
    mime_type?: string;
    duration?: number;
    title?: string;
    performer?: string;
    emoji?: string;
  }>;
}

interface Channel {
  id: string;
  name: string;
  type: string;
  status: string;
}



const WhatsApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const outletContext = useOutletContext<{ toggleDrawer?: () => void }>();
  
  // Constantes para URLs da API e WebSocket
  const API_BASE_URL = getApiUrl();
  const WS_URL = getWebSocketUrl();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const selectedConversationRef = useRef<string>('');
  
  // Atualizar ref quando selectedConversation mudar
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState<string | null>(null);
  const [deletingContact, setDeletingContact] = useState<string | null>(null);
  const [openLeadDetailsDialog, setOpenLeadDetailsDialog] = useState(false);
  const [openHealthcarePanel, setOpenHealthcarePanel] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  // Attach image to patient gallery
  const [attachImageUrl, setAttachImageUrl] = useState<string | null>(null);
  const [attachingToGallery, setAttachingToGallery] = useState(false);
  const [attachGalleryMsg, setAttachGalleryMsg] = useState<string>('');
  const [stage4Leads, setStage4Leads] = useState<Set<string>>(new Set());
  const [allLeadsStages, setAllLeadsStages] = useState<Map<string, number>>(new Map());
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [replyingToMessage, setReplyingToMessage] = useState<Message | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [crmStages, setCrmStages] = useState<Array<{ id: string; name: string; stage_order: number; color: string }>>([]);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState<{ el: HTMLElement; messageId: string } | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [inboxFilterTagIds, setInboxFilterTagIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLDivElement>(null);
  const recordingIntervalRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  /** Ao cancelar durante gravação, não criar ficheiro no onstop */
  const discardRecordingRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollContainerRef = useRef<HTMLDivElement>(null); // Ref para o container de scroll
  const lastMessagesRef = useRef<string>(''); // Para evitar atualizações desnecessárias
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 3; // Limitar tentativas de reconexão

  // Abrir conversa específica se contactId estiver na URL
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (contactId) {
      // Se há um contactId na URL, buscar conversas diretamente desse contato
      const fetchConversationForContact = async () => {
        try {
          console.log('🔍 Buscando conversa para contactId:', contactId);
          
          // Buscar conversas específicas desse contato no WhatsApp
          const contactConversationResponse = await axios.get(
            `${API_BASE_URL}/conversations?channel=whatsapp&contact_id=${contactId}&limit=10`
          );
          
          if (contactConversationResponse.data.success) {
            const contactConversations = contactConversationResponse.data.data || [];
            
            if (contactConversations.length > 0) {
              // Pegar a conversa mais recente desse contato
              const mostRecentConversation = contactConversations.sort((a: any, b: any) => {
                const dateA = new Date(a.last_message_at || a.updated_at || a.created_at || 0);
                const dateB = new Date(b.last_message_at || b.updated_at || b.created_at || 0);
                return dateB.getTime() - dateA.getTime();
              })[0];
              
              console.log('✅ Conversa encontrada:', mostRecentConversation.id);
              setSelectedConversation(mostRecentConversation.id);
              
              // IMPORTANTE: Carregar TODAS as mensagens desta conversa quando há contactId na URL
              // Isso garante que mensagens antigas sejam carregadas mesmo que não estejam entre as últimas 50
              await fetchMessages(mostRecentConversation.id, true); // true = loadAll
              
              // Buscar também as outras conversas do WhatsApp para exibir na lista
              const allConversationsResponse = await axios.get(
                `${API_BASE_URL}/conversations?channel=whatsapp&limit=50&page=1`
              );
              
              if (allConversationsResponse.data.success) {
                const allConversations = allConversationsResponse.data.data || [];
                
                // Garantir que a conversa selecionada esteja na lista
                const conversationExists = allConversations.some((c: any) => c.id === mostRecentConversation.id);
                if (!conversationExists) {
                  allConversations.unshift(mostRecentConversation);
                }
                
                setConversations(allConversations);
              }
            } else {
              console.warn('⚠️ Nenhuma conversa encontrada para este contato');
              // Buscar todas as conversas normalmente
              const response = await axios.get(`${API_BASE_URL}/conversations?channel=whatsapp&limit=50&page=1`);
              if (response.data.success) {
                setConversations(response.data.data || []);
              }
            }
            
            // Limpar o parâmetro da URL após abrir a conversa
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('contactId');
            window.history.replaceState({}, '', newUrl.toString());
          }
        } catch (error) {
          console.error('❌ Erro ao buscar conversas do WhatsApp:', error);
          // Em caso de erro, buscar todas as conversas normalmente
          try {
            const response = await axios.get(`${API_BASE_URL}/conversations?channel=whatsapp&limit=50&page=1`);
            if (response.data.success) {
              setConversations(response.data.data || []);
            }
          } catch (err) {
            console.error('❌ Erro ao buscar conversas após falha:', err);
          }
        }
      };
      
      fetchConversationForContact();
    }
  }, [searchParams]);

  // Função para gerar cor do avatar baseada no nome
  const getAvatarColor = (name: string) => {
    const colors = [
      '#1976d2', '#dc004e', '#9c27b0', '#673ab7', '#3f51b5',
      '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
      '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
      '#ff5722', '#795548', '#607d8b'
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Função para atualizar avatar do contato
  const updateContactAvatar = async (contactId: string) => {
    try {
      setUpdatingAvatar(contactId);
      const response = await axios.post(`${API_BASE_URL}/contacts/${contactId}/update-avatar`);
      
      if (response.data.success) {
        // Atualizar a lista de conversas para refletir a mudança
        await fetchConversations();
        
        // Se a conversa estiver selecionada, atualizar as mensagens também
        if (selectedConversation) {
          await fetchMessages(selectedConversation);
        }
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      setError('Erro ao atualizar foto de perfil');
    } finally {
      setUpdatingAvatar(null);
    }
  };

  // Função para excluir contato
  const deleteContact = async (contactId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setDeletingContact(contactId);
      const response = await axios.delete(`${API_BASE_URL}/contacts/${contactId}`);
      
      if (response.data.success) {
        // Remover a conversa da lista
        setConversations(prev => prev.filter(conv => conv.contact_id !== contactId));
        
        // Se a conversa excluída estava selecionada, limpar a seleção
        if (selectedConversation && conversations.find(c => c.id === selectedConversation)?.contact_id === contactId) {
          setSelectedConversation('');
          setMessages([]);
        }
        
        setError('');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Erro ao excluir contato');
    } finally {
      setDeletingContact(null);
    }
  };

  // Função para scroll automático para a última mensagem
  const scrollToBottom = () => {
    // Scroll apenas no container de mensagens, não na página toda
    if (messagesScrollContainerRef.current) {
      messagesScrollContainerRef.current.scrollTo({
        top: messagesScrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    } else if (messagesEndRef.current) {
      // Fallback: scrollIntoView apenas se não encontrar container
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  // Função para normalizar URLs de anexos
  const normalizeAttachmentUrl = (url: string | undefined): string => {
    if (!url) return '';
    // Se já é uma URL completa (http/https), retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Se for URL relativa, construir URL completa usando a base da API
    const apiBase = API_BASE_URL.replace('/api/v1', '');
    return url.startsWith('/') ? `${apiBase}${url}` : `${apiBase}/${url}`;
  };

  // Função para normalizar attachments (parse JSON se necessário)
  const normalizeAttachments = (attachments: any): Array<any> => {
    if (!attachments) return [];
    if (typeof attachments === 'string') {
      try {
        return JSON.parse(attachments);
      } catch {
        return [];
      }
    }
    if (Array.isArray(attachments)) return attachments;
    return [];
  };


  // Função para buscar conversas (memoizada)
  const fetchConversations = useCallback(async (reset: boolean = true) => {
    try {
      setError('');
      
      if (reset) {
        setConversationsPage(1);
        setHasMoreConversations(true);
      }
      
      const page = reset ? 1 : conversationsPage;
      const limit = 100; // Reduzir de 200 para 100 para carregar mais rápido
      
      const archivedParam = showArchived ? '&includeArchived=true' : '';
      const tagParam = inboxTagFilterQuery(inboxFilterTagIds);
      const url = `${API_BASE_URL}/conversations?channel=whatsapp&limit=${limit}&page=${page}&sortBy=last_message_at&sortOrder=desc${archivedParam}${tagParam}`;
      console.log('🌐 [WhatsApp Onboarding] Buscando conversas:', url);
      
      const conversationsResponse = await axios.get(url, {
        timeout: 30000
      });
      
      if (conversationsResponse.data.success) {
        const newConversations: Conversation[] = conversationsResponse.data.data || [];
        const pagination = conversationsResponse.data.pagination;
        
        // Log detalhado das conversas recebidas
        console.log('📦 [WhatsApp Onboarding] Conversas recebidas:', newConversations.length);
        if (newConversations.length > 0) {
          console.log('📋 [WhatsApp Onboarding] Primeiras 3 conversas:', newConversations.slice(0, 3).map(c => ({
            id: c.id,
            canal_tipo: c.channel?.type,
            canal_nome: c.channel?.name,
            contato: c.contact?.name
          })));
        }
        
        // Filtrar conversas vazias (sem last_message_at válido)
        const conversationsWithMessages = newConversations.filter((conv: any) => {
          return conv.last_message_at && conv.last_message_at !== null;
        });
        
        console.log(`🔍 [WhatsApp Onboarding] Conversas antes do filtro: ${newConversations.length}, depois: ${conversationsWithMessages.length}`);
        
        // Usar apenas conversas reais da API
        const finalConversations = conversationsWithMessages;
        
        // Verificar se há mais conversas para carregar
        const hasMore = pagination ? page < pagination.totalPages : finalConversations.length === limit;
        setHasMoreConversations(hasMore);
        
        // Ordenar conversas por last_message_at (mais recentes primeiro)
        const sortedNewConversations = finalConversations.sort((a: any, b: any) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        if (reset) {
          setConversations(sortedNewConversations);
          // Verificar estágios de todos os leads
          await checkAllLeadsStages(sortedNewConversations);
        } else {
          // Adicionar novas conversas às existentes, evitando duplicatas
          setConversations(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = sortedNewConversations.filter(c => !existingIds.has(c.id));
            // Evitar duplicação: verificar também por contact_id e channel_id
            const existingKeys = new Set(prev.map(c => `${c.contact_id}-${c.channel_id}`));
            const trulyUnique = uniqueNew.filter(c => !existingKeys.has(`${c.contact_id}-${c.channel_id}`));
            const combined = [...prev, ...trulyUnique].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
            return combined;
          });
        }
        
        // Atualizar página
        setConversationsPage(page + 1);
      }
    } catch (err) {
      console.error('Erro ao buscar conversas:', err);
      // Em caso de erro, mostrar lista vazia e mensagem de erro
      setConversations([]);
      setHasMoreConversations(false);
      
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar conversas. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setLoading(false);
      setLoadingMoreConversations(false);
    }
  }, [conversationsPage, showArchived, inboxFilterTagIds]);

  // Função para carregar mais conversas
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreConversations || !hasMoreConversations) return;
    
    setLoadingMoreConversations(true);
    await fetchConversations(false);
  }, [loadingMoreConversations, hasMoreConversations, fetchConversations]);

  // Função para marcar conversa como lida
  const markConversationAsRead = useCallback(async (conversationId: string) => {
    try {
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId.startsWith('instance-')) {
        console.log('📱 Marcando conversa de instância como lida:', conversationId);
        // Para conversas de instância, apenas atualizar o estado local
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unread_count: 0 }
              : conv
          );
          console.log('📱 Estado atualizado:', updated.find(c => c.id === conversationId));
          return updated;
        });
        return;
      }

      await axios.post(`${API_BASE_URL}/conversations/${conversationId}/mark-as-read`);
      
      // Atualizar a lista de conversas para refletir a mudança
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
    } catch (err) {
      console.error('Erro ao marcar conversa como lida:', err);
      // Não mostrar erro para o usuário, pois não é crítico
    }
  }, []);

  // Função para arquivar conversa
  const handleArchiveConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/conversations/${conversationId}`, {
        status: 'archived'
      });
      
      // Atualizar estado local
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // Se a conversa arquivada estava selecionada, limpar seleção
      if (selectedConversation === conversationId) {
        setSelectedConversation('');
        setMessages([]);
      }
    } catch (err) {
      console.error('Erro ao arquivar conversa:', err);
      setError('Erro ao arquivar conversa');
    }
  }, [selectedConversation]);

  // Função para desarquivar conversa
  const handleUnarchiveConversation = useCallback(async (conversationId: string) => {
    try {
      await axios.patch(`${API_BASE_URL}/conversations/${conversationId}`, {
        status: 'open'
      });
      
      // Buscar conversa atualizada do backend
      const response = await axios.get(`${API_BASE_URL}/conversations/${conversationId}`);
      if (response.data.success) {
        const updatedConversation = response.data.data;
        setConversations(prev => {
          const exists = prev.find(c => c.id === conversationId);
          if (exists) {
            return prev.map(c => c.id === conversationId ? updatedConversation : c);
          } else {
            return [...prev, updatedConversation].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          }
        });
      }
    } catch (err) {
      console.error('Erro ao desarquivar conversa:', err);
      setError('Erro ao desarquivar conversa');
    }
  }, []);

  // Log das mensagens quando mudarem
  useEffect(() => {
    console.log(`🎨 Mensagens no estado: ${messages.length}`);
    messages.forEach((message, index) => {
      console.log(`📝 Mensagem ${index + 1}/${messages.length}: ${message.content?.substring(0, 30) || '(sem conteúdo)'}...`);
    });
  }, [messages]);


  // Função para buscar mensagens de uma conversa
  const fetchMessages = useCallback(async (conversationId: string, loadAll: boolean = false) => {
    try {
      console.log(`🔍 Buscando mensagens para conversa: ${conversationId}, loadAll: ${loadAll}`);
      
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId.startsWith('instance-')) {
        console.log('📱 Conversa de instância detectada, buscando mensagens das instâncias...');
        
        // Extrair contactId e instanceName do ID da conversa
        // O formato é: instance-{contactId}-{instanceName}
        const instanceKey = conversationId.replace('instance-', '');
        
        // O contactId é um UUID completo (36 caracteres: 8-4-4-4-12)
        // Então precisamos pegar os primeiros 36 caracteres como contactId
        const contactId = instanceKey.substring(0, 36);
        const instanceName = instanceKey.substring(37); // Pular o hífen após o UUID
        
        console.log('🔍 Extraído:', { contactId, instanceName, originalId: conversationId });
        
        // Buscar mensagens das instâncias WhatsApp
        const response = await axios.get(`${API_BASE_URL}/whatsapp-instance-messages?contact_id=${contactId}&instance_name=${instanceName}&limit=1000`, {
          timeout: 30000
        });
        
        if (response.data.success) {
          const messagesData: Message[] = response.data.data || [];
          console.log(`📋 Total de mensagens de instância recebidas: ${messagesData.length}`);
          
          // Normalizar attachments em todas as mensagens
          const normalizedMessages = messagesData.map(msg => ({
            ...msg,
            attachments: normalizeAttachments(msg.attachments)
          }));
          
          // Ordenar mensagens por data (mais antigas primeiro, mais novas no final)
          const sortedMessages = normalizedMessages.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
          console.log(`📋 Mensagens de instância ordenadas: ${sortedMessages.length}`);
          
          setMessages(sortedMessages);
          console.log(`🎯 Mensagens de instância definidas no estado: ${sortedMessages.length}`);
          
          // Scroll para a última mensagem após um pequeno delay (apenas uma vez)
          messagesLoadedRef.current = conversationId;
          requestAnimationFrame(() => {
            setTimeout(() => {
              console.log('🔄 Executando scroll para baixo...');
              scrollToBottom();
            }, 100);
          });
        } else {
          console.warn('Nenhuma mensagem encontrada para a conversa de instância');
          setMessages([]);
        }
        return;
      }
      
      // Para conversas normais, usar o endpoint padrão
      // Se loadAll=true, usar limit muito alto para sinalizar ao backend que deve carregar tudo
      const limit = loadAll ? 10000 : 1000;
      const response = await axios.get(`${API_BASE_URL}/messages/conversation/${conversationId}?limit=${limit}&sortOrder=asc`, {
        timeout: 30000 // 30 segundos de timeout
      });
      
      logger.apiResponse('fetchMessages', response.data);
      const messagesData: Message[] = response.data.data || [];
      console.log(`📋 Total de mensagens recebidas: ${messagesData.length}`);
      
      // Normalizar attachments em todas as mensagens
      const normalizedMessages = messagesData.map(msg => ({
        ...msg,
        attachments: normalizeAttachments(msg.attachments)
      }));
      
      // Ordenar mensagens por data (mais antigas primeiro, mais novas no final)
      const sortedMessages = normalizedMessages.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
      console.log(`📋 Mensagens ordenadas: ${sortedMessages.length}`);
      
      // Verificar se as mensagens são diferentes das anteriores para evitar atualizações desnecessárias
      const messagesKey = sortedMessages.map(m => m.id).join(',');
      if (lastMessagesRef.current === messagesKey && messages.length === sortedMessages.length) {
        console.log('📋 Mensagens já estão atualizadas, pulando setMessages');
        messagesLoadedRef.current = conversationId;
        return;
      }
      
      lastMessagesRef.current = messagesKey;
      setMessages(sortedMessages);
      console.log(`🎯 Mensagens definidas no estado: ${sortedMessages.length}`);
      
      // Scroll para a última mensagem após um pequeno delay (apenas uma vez)
      messagesLoadedRef.current = conversationId;
      requestAnimationFrame(() => {
        setTimeout(() => {
          console.log('🔄 Executando scroll para baixo...');
          scrollToBottom();
        }, 100);
      });
    } catch (err) {
      console.error('Erro ao buscar mensagens:', err);
      // Em caso de erro, mostrar lista vazia
      setMessages([]);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar mensagens. Verifique sua conexão e tente novamente.');
      }
    }
  }, [conversations]);

  // Função para deletar mensagem
  const handleDeleteMessage = async (message: Message) => {
    if (!selectedConversation) {
      setError('Conversa não selecionada');
      setMessageMenuAnchor(null);
      return;
    }

    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
    if (!contact?.phone) {
      setError('Telefone do contato não encontrado');
      setMessageMenuAnchor(null);
      return;
    }

    // Buscar messageId em múltiplos lugares
    // Primeiro tentar external_id direto, depois metadata
    const messageIdToDelete = message.external_id || 
                              message.metadata?.messageId || 
                              message.metadata?.zaapId || 
                              message.metadata?.external_id ||
                              null;

    // Se não encontrou messageId, ainda tentar deletar (backend pode buscar)
    // Mas passar null para o backend decidir
    setDeletingMessageId(message.id);
    try {
      const response = await axios.delete(`${API_BASE_URL}/messages/${message.id}`, {
        data: {
          messageId: messageIdToDelete, // Pode ser null, backend vai buscar se necessário
          phone: contact.phone.replace(/\D/g, ''), // Remove formatação
          owner: message.direction === 'outbound' // true se foi enviada por nós
        }
      });

      if (response.data.success) {
        // Remover mensagem do estado local
        setMessages(prev => prev.filter(m => m.id !== message.id));
        setMessageMenuAnchor(null);
      } else {
        throw new Error(response.data.error || 'Erro ao deletar mensagem');
      }
    } catch (err: any) {
      console.error('Erro ao deletar mensagem:', err);
      const errorMessage = err.response?.data?.error || 'Erro ao deletar mensagem';
      setError(errorMessage);
      // Não fechar o menu se houver erro, para o usuário ver a mensagem
    } finally {
      setDeletingMessageId(null);
    }
  };

  // Função para enviar mensagem
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedConversation || sendingMessage) return;

    // Se houver arquivos selecionados, enviar como mídia
    if (selectedFiles.length > 0) {
      await handleSendMultipleMedia(selectedFiles);
      return;
    }


    // Caso contrário, enviar mensagem de texto normal
    setSendingMessage(true);
    try {
      // Buscar messageId para responder (pode estar em external_id ou metadata)
      const replyMessageId = replyingToMessage?.external_id || 
                             replyingToMessage?.metadata?.messageId || 
                             replyingToMessage?.metadata?.zaapId || 
                             replyingToMessage?.metadata?.external_id;
      
      const response = await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: selectedConversation,
        content: newMessage,
        content_type: 'text',
        messageId: replyMessageId || undefined
      });

      setNewMessage('');
      setReplyingToMessage(null); // Limpar mensagem sendo respondida

      // Adicionar mensagem imediatamente ao estado (otimistic update)
      if (response.data.success && response.data.data) {
        const sentMessage = response.data.data;
        // Garantir que attachments seja sempre um array
        const messageWithAttachments = {
          ...sentMessage,
          attachments: Array.isArray(sentMessage.attachments) 
            ? sentMessage.attachments 
            : (typeof sentMessage.attachments === 'string' 
                ? (() => {
                    try {
                      return JSON.parse(sentMessage.attachments);
                    } catch {
                      return [];
                    }
                  })()
                : [])
        };

        setMessages(prev => {
          // Verificar se a mensagem já existe (evitar duplicatas do WebSocket)
          const exists = prev.some(msg => msg.id === messageWithAttachments.id);
          if (exists) {
            return prev;
          }
          const newMessages = [...prev, messageWithAttachments];
          // Ordenar mensagens por data de envio (mais antigas primeiro)
          return newMessages.sort((a, b) => 
            new Date(a.sent_at || a.created_at).getTime() - new Date(b.sent_at || b.created_at).getTime()
          );
        });

        // Atualizar conversa na lista
        setConversations(prev => 
          prev.map(conv => 
            conv.id === selectedConversation
              ? {
                  ...conv,
                  last_message_at: messageWithAttachments.sent_at || messageWithAttachments.created_at,
                  last_message: newMessage
                }
              : conv
          ).sort((a, b) => {
            const dateA = new Date(a.last_message_at || a.updated_at || 0);
            const dateB = new Date(b.last_message_at || b.updated_at || 0);
            return dateB.getTime() - dateA.getTime();
          })
        );

        // Scroll para o final após adicionar mensagem
        setTimeout(scrollToBottom, 100);
      }

      // Manter foco no campo de mensagem após envio
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para enviar mídia
  const handleSendMedia = async (file: File) => {
    if (!selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', selectedConversation);
      if (newMessage.trim()) {
        formData.append('content', newMessage);
      }
      // Se for um arquivo de áudio gravado
      if (file.name.includes('recording') || file.name.includes('voice')) {
        formData.append('is_voice', 'true');
      }

      const response = await axios.post(`${API_BASE_URL}/messages/send-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Sempre recarregar mensagens após envio bem-sucedido para garantir que apareçam
      setNewMessage('');
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Aguardar um pouco para garantir que o banco foi atualizado e WebSocket processou
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);

      // Manter foco no campo de mensagem após envio
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (err: any) {
      console.error('Erro ao enviar mídia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para enviar múltiplos arquivos
  const handleSendMultipleMedia = async (files: File[]) => {
    if (!selectedConversation || sendingMessage || files.length === 0) return;

    setSendingMessage(true);
    // Limpar prévia imediatamente ao clicar em enviar
    const messageToSend = newMessage;
    // Buscar messageId para responder (pode estar em external_id ou metadata)
    const replyingToId = replyingToMessage?.external_id || 
                         replyingToMessage?.metadata?.messageId || 
                         replyingToMessage?.metadata?.zaapId || 
                         replyingToMessage?.metadata?.external_id;
    setNewMessage('');
    setSelectedFiles([]);
    setReplyingToMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      // Enviar cada arquivo sequencialmente
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('conversation_id', selectedConversation);
        // Adicionar mensagem apenas no primeiro arquivo
        if (i === 0 && messageToSend.trim()) {
          formData.append('content', messageToSend);
        }
        // Adicionar messageId se estiver respondendo (apenas no primeiro arquivo)
        if (i === 0 && replyingToId) {
          formData.append('messageId', replyingToId);
        }
        // Se for um arquivo de áudio gravado
        if (file.name.includes('recording') || file.name.includes('voice')) {
          formData.append('is_voice', 'true');
        }

        await axios.post(`${API_BASE_URL}/messages/send-media`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Pequeno delay entre envios para não sobrecarregar
        if (i < files.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Aguardar um pouco para garantir que o banco foi atualizado e WebSocket processou
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);

      // Manter foco no campo de mensagem após envio
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    } catch (err: any) {
      console.error('Erro ao enviar mídia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para lidar com paste de imagens (Ctrl+V)
  const handlePasteImage = useCallback((e: React.ClipboardEvent) => {
    if (!selectedConversation) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Verificar se é uma imagem
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        
        if (file) {
          // Criar um nome descritivo para o arquivo
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const renamedFile = new File(
            [file],
            `pasted-image-${timestamp}.png`,
            { type: file.type }
          );
          
          // Adicionar aos arquivos selecionados (mostrará preview)
          setSelectedFiles(prev => [...prev, renamedFile]);
        }
        break;
      }
    }
  }, [selectedConversation]);

  // Função para selecionar arquivos (múltiplos)
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/mpeg', 'video/quicktime',
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/aac', 'audio/m4a',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain', 'application/zip'
    ];

    const isValidFileType = (file: File) => {
      const ext = (file.name.toLowerCase().split('.').pop() || '');
      const isPdf = ext === 'pdf' && (file.type === 'application/pdf' || !file.type);
      const isDoc = (ext === 'doc' || ext === 'docx') && (['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ''].includes(file.type));
      const isExcel = (ext === 'xls' || ext === 'xlsx') && (['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ''].includes(file.type));
      const isPpt = (ext === 'ppt' || ext === 'pptx') && (['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ''].includes(file.type));
      const isTxt = ext === 'txt' && (file.type === 'text/plain' || !file.type);
      const isZip = ext === 'zip' && (file.type === 'application/zip' || !file.type);
      return allowedTypes.includes(file.type) || isPdf || isDoc || isExcel || isPpt || isTxt || isZip;
    };

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        errors.push(`${file.name}: Arquivo muito grande. Tamanho máximo: 50MB`);
        return;
      }
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: Tipo de arquivo não suportado`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError('');
    }
  };

  // Contador da gravação: efecto apenas [isRecording] — evitar limpar o interval quando o recorder é atribuído.
  useEffect(() => {
    if (!isRecording) return;
    recordingIntervalRef.current = window.setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
    return () => {
      if (recordingIntervalRef.current != null) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
  }, [isRecording]);

  // Função para iniciar gravação de áudio
  const handleStartRecording = async () => {
    try {
      discardRecordingRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;

      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
      ];
      const mimeType = mimeCandidates.find((t) => MediaRecorder.isTypeSupported(t));

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        try {
          recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        } catch {
          /* noop */
        }
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;

        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          return;
        }

        const outType = recorder.mimeType?.split(';')[0] || 'audio/webm';
        const audioBlob = new Blob(chunks, { type: outType });
        const ext = outType.includes('mp4') ? 'm4a' : 'webm';
        const audioFile = new File([audioBlob], `recording-${Date.now()}.${ext}`, { type: outType });
        setSelectedFiles([audioFile]);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecordingTime(0);
      setIsRecording(true);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setError('Erro ao acessar microfone. Verifique as permissões.');
    }
  };

  // Função para parar gravação de áudio
  const handleStopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    try {
      rec.stop();
    } catch {
      /* noop */
    }
    setIsRecording(false);
  };

  // Função para cancelar arquivo selecionado
  const handleCancelFile = (index?: number) => {
    if (index !== undefined) {
      // Remover arquivo específico
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      // Remover todos os arquivos
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (isRecording) {
        discardRecordingRef.current = true;
        handleStopRecording();
      }
    }
  };

  // Desmontagem: largar microfone e timers
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current != null) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      discardRecordingRef.current = true;
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      recordingStreamRef.current = null;
    };
  }, []);

  // WebSocket para tempo real
  useEffect(() => {
    // Conectar ao WebSocket apenas uma vez
    if (!socketRef.current) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      console.log('🔌 Conectando ao WebSocket...');
      socketRef.current = io(WS_URL, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 2000,
        reconnectionAttempts: 5,
        // Token é opcional agora
        ...(token && { auth: { token: token } })
      });

      socketRef.current.on('connect', () => {
        logger.websocket('connected', 'WhatsApp');
      });

      socketRef.current.on('connect_error', (error) => {
        logger.error('Erro de conexão WebSocket:', error);
        // Não bloquear conexão por erro de autenticação - WebSocket agora funciona sem token
      });

      socketRef.current.on('new_message', (data) => {
        logger.websocket('new_message', {
          conversationId: data.message?.conversation_id || data.conversation_id,
          direction: data.message?.direction || data.direction,
        });
        
        let message = data.message || data;
        
        if (!message.conversation_id) {
          message.conversation_id = data.conversation_id || message.conversation_id;
        }
        
        if (message.attachments) {
          if (Array.isArray(message.attachments)) {
            message = { ...message, attachments: message.attachments };
          } else if (typeof message.attachments === 'string') {
            try {
              message = { ...message, attachments: JSON.parse(message.attachments) };
            } catch (e) {
              message = { ...message, attachments: [] };
            }
          } else {
            message = { ...message, attachments: [] };
          }
        } else {
          message = { ...message, attachments: [] };
        }

        // If this message belongs to the active conversation, add it to the chat
        const currentConversationId = selectedConversationRef.current;
        if (message.conversation_id && message.conversation_id === currentConversationId) {
          setMessages(prev => {
            const exists = prev.some(msg => msg.id === message.id);
            if (exists) return prev;
            const newMessages = [...prev, message];
            return newMessages.sort((a, b) => 
              new Date(a.sent_at || a.created_at).getTime() - new Date(b.sent_at || b.created_at).getTime()
            );
          });
          setTimeout(scrollToBottom, 100);
        }
        
        // Channel filter: skip if clearly not WhatsApp
        const messageChannelType = message.channel?.type || data.channel?.type;
        if (messageChannelType && messageChannelType !== 'whatsapp') {
          return;
        }
        
        // Update conversation list: reorder + badges (uses functional state to avoid stale closure)
        setConversations(prev => {
          // Channel check using current state (not stale closure)
          if (!messageChannelType) {
            const existingConv = prev.find(c => c.id === message.conversation_id);
            if (existingConv && existingConv.channel?.type !== 'whatsapp') {
              return prev;
            }
          }
          
          const conversationExists = prev.some(conv => conv.id === message.conversation_id);
          
          if (conversationExists) {
            const updatedConversations = prev.map(conv => {
              if (conv.id === message.conversation_id) {
                const isCurrentlySelected = conv.id === selectedConversationRef.current;
                return {
                  ...conv,
                  last_message_at: message.sent_at || message.created_at || new Date().toISOString(),
                  last_message: message.content ? message.content.substring(0, 100) : conv.last_message,
                  unread_count: (message.direction === 'inbound' && !isCurrentlySelected) 
                    ? (conv.unread_count || 0) + 1 
                    : (isCurrentlySelected ? 0 : conv.unread_count || 0)
                };
              }
              return conv;
            });
            
            return updatedConversations.sort((a, b) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          } else {
            // New conversation not yet in list - fetch from API
            axios.get(`${API_BASE_URL}/conversations/${message.conversation_id}`)
              .then(response => {
                if (response.data.success && response.data.data) {
                  const newConversation = response.data.data;
                  if (newConversation.channel?.type === 'whatsapp') {
                    setConversations(innerPrev => {
                      if (innerPrev.some(c => c.id === newConversation.id)) return innerPrev;
                      const updated = [newConversation, ...innerPrev];
                      return updated.sort((a, b) => {
                        const dateA = new Date(a.last_message_at || a.updated_at || 0);
                        const dateB = new Date(b.last_message_at || b.updated_at || 0);
                        return dateB.getTime() - dateA.getTime();
                      });
                    });
                  }
                }
              })
              .catch(() => {});
            
            return prev;
          }
        });
      });

      socketRef.current.on('conversation_updated', (data: any) => {
        logger.websocket('conversation_updated', data);
        
        if (data && data.conversationId && data.update) {
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.id === data.conversationId) {
                return {
                  ...conv,
                  last_message_at: data.update.last_message_at || conv.last_message_at,
                  unread_count: data.update.unread_count !== undefined ? data.update.unread_count : conv.unread_count,
                  status: data.update.status || conv.status
                };
              }
              return conv;
            });
            
            return updated.sort((a, b) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          });
        }
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('⚠️ WebSocket desconectado:', reason);
        
        // Não tentar reconectar automaticamente se foi por erro de autenticação
        // Apenas reconectar se foi desconexão normal (transport close sem erro de auth)
        if (reason === 'transport close' && reconnectAttemptsRef.current < maxReconnectAttempts) {
          console.log('🔄 Reconexão automática habilitada para desconexão normal');
        } else if (reason === 'io server disconnect') {
          console.log('⚠️ Servidor desconectou o WebSocket. Não reconectando automaticamente.');
        }
      });
    }

    // Entrar na sala da conversa quando selectedConversation mudar
    if (socketRef.current && selectedConversation) {
      socketRef.current.emit('join_conversation', selectedConversation);
    }

    return () => {
      // Não desconectar aqui, apenas sair da sala
      if (socketRef.current && selectedConversation) {
        socketRef.current.emit('leave_conversation', selectedConversation);
      }
    };
  }, [selectedConversation]); // Manter apenas selectedConversation como dependência

  // Carregar conversas iniciais e ao mudar filtro de tags/arquivadas
  useEffect(() => {
    fetchConversations(true);
  }, [showArchived, inboxFilterTagIds]);

  // Ref para evitar múltiplas chamadas de fetchMessages
  const fetchingMessagesRef = useRef<string | null>(null);
  const messagesLoadedRef = useRef<string | null>(null);

  // Carregar mensagens quando uma conversa é selecionada
  useEffect(() => {
    if (selectedConversation && fetchingMessagesRef.current !== selectedConversation) {
      fetchingMessagesRef.current = selectedConversation;
      messagesLoadedRef.current = null;
      lastMessagesRef.current = ''; // Limpar referência de mensagens anteriores
      
      // Limpar mensagens anteriores antes de carregar novas
      setMessages([]);
      
      fetchMessages(selectedConversation).finally(() => {
        fetchingMessagesRef.current = null;
        messagesLoadedRef.current = selectedConversation;
      });
      
      // Marcar conversa como lida quando selecionada
      markConversationAsRead(selectedConversation);
    }
  }, [selectedConversation]); // Remover dependências que causam loops

  // Scroll automático apenas quando mensagens forem carregadas pela primeira vez
  useEffect(() => {
    if (messages.length > 0 && messagesLoadedRef.current === selectedConversation) {
      // Usar requestAnimationFrame para scroll suave sem piscar
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToBottom();
        }, 50);
      });
    }
  }, [messages.length, selectedConversation]); // Usar apenas length para evitar re-renderizações

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  const handleContactClick = async (contact: Contact) => {
    setSelectedContact(contact);
    setEditingName(contact.name || '');
    setIsEditingName(false);
    // Abrir o Painel de Saúde (Healthcare Panel)
    setOpenHealthcarePanel(true);
  };

  const handleAttachImageToGallery = async (imageUrl: string) => {
    const currentContact = conversations.find(c => c.id === selectedConversation)?.contact;
    if (!currentContact) {
      setAttachGalleryMsg('Nenhum contato encontrado para esta conversa');
      setAttachImageUrl(null);
      return;
    }
    setAttachingToGallery(true);
    try {
      // Verificar se contato tem paciente vinculado
      const { data: panelRes } = await axios.get(`${API_BASE_URL}/clinic/healthcare-panel/contact/${currentContact.id}/summary`);
      if (!panelRes?.success || !panelRes?.data?.linked_patient) {
        setAttachGalleryMsg('Nenhum paciente vinculado a este contato. Vincule um paciente no Painel de Saúde primeiro.');
        setAttachImageUrl(null);
        return;
      }
      const patientId = panelRes.data.linked_patient.id;
      const { data: res } = await axios.post(`${API_BASE_URL}/clinic/patient-images/from-chat`, {
        patient_id: patientId,
        image_url: imageUrl,
        description: `Imagem do chat`,
      });
      if (res.success) {
        setAttachGalleryMsg('Imagem salva no banco de imagens do paciente!');
      } else {
        setAttachGalleryMsg('Erro ao salvar imagem');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Erro ao salvar imagem na galeria';
      setAttachGalleryMsg(msg);
    } finally {
      setAttachingToGallery(false);
      setAttachImageUrl(null);
    }
  };

  const handleSendMediaFromHealthcare = async (url: string, type: string, fileName?: string) => {
    if (!selectedConversation) return;
    try {
      setSendingMessage(true);
      await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: selectedConversation,
        content: fileName || 'Imagem clínica',
        media_type: type,
        media_url: url,
      });
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);
    } catch (err: any) {
      console.error('Erro ao enviar mídia do healthcare:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAttachDocToHealthcare = async (docUrl: string, fileName: string, mimeType?: string) => {
    const currentContact = conversations.find(c => c.id === selectedConversation)?.contact;
    if (!currentContact) {
      setAttachGalleryMsg('Nenhum contato encontrado para esta conversa');
      return;
    }
    setAttachingToGallery(true);
    try {
      const { data: panelRes } = await axios.get(`${API_BASE_URL}/clinic/healthcare-panel/contact/${currentContact.id}/summary`);
      if (!panelRes?.success || !panelRes?.data?.linked_patient) {
        setAttachGalleryMsg('Nenhum paciente vinculado a este contato. Vincule um paciente no Painel de Saúde primeiro.');
        return;
      }
      const patientId = panelRes.data.linked_patient.id;
      const { data: res } = await axios.post(`${API_BASE_URL}/clinic/patients/${patientId}/documents/from-chat`, {
        patient_id: patientId,
        document_url: docUrl,
        file_name: fileName,
        file_type: mimeType || 'application/octet-stream',
        description: `Documento do chat`,
      });
      if (res.success) {
        setAttachGalleryMsg('Documento salvo nos docs do paciente com sucesso!');
      } else {
        setAttachGalleryMsg('Erro ao salvar documento');
      }
    } catch (err: any) {
      setAttachGalleryMsg(err?.response?.data?.error || 'Erro ao salvar documento nos docs do paciente');
    } finally {
      setAttachingToGallery(false);
    }
  };

  const handleOpenLegacyDetails = async (contact: Contact) => {
    setSelectedContact(contact);
    setEditingName(contact.name || '');
    setIsEditingName(false);
    setOpenLeadDetailsDialog(true);
    await fetchCrmStages();
    setTimeout(() => {
      fetchCurrentStage(contact.id);
    }, 200);
  };
  
  const fetchCrmStages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/crm/stages`);
      if (response.data.success && response.data.data) {
        const stages = response.data.data
          .map((stage: any) => ({
            id: stage.id,
            name: stage.name,
            stage_order: stage.stage_order,
            color: stage.color || '#6B7280'
          }))
          .sort((a: any, b: any) => a.stage_order - b.stage_order);
        setCrmStages(stages);
      }
    } catch (err: any) {
      console.error('Erro ao buscar estágios do CRM:', err);
    }
  };
  
  const fetchCurrentStage = async (contactId: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/crm/contacts/stages`, {
        contact_ids: [contactId]
      });
      if (response.data.success && response.data.data) {
        const stageOrder = response.data.data[contactId];
        if (stageOrder) {
          // Aguardar um pouco para garantir que os estágios foram carregados
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Buscar o ID do estágio pelo stage_order
          let stage = crmStages.find(s => s.stage_order === stageOrder);
          
          if (!stage && crmStages.length > 0) {
            // Se não encontrar, buscar novamente os estágios
            const updatedStagesResponse = await axios.get(`${API_BASE_URL}/crm/stages`);
            if (updatedStagesResponse.data.success && updatedStagesResponse.data.data) {
              const stages = updatedStagesResponse.data.data
                .map((s: any) => ({
                  id: s.id,
                  name: s.name,
                  stage_order: s.stage_order,
                  color: s.color || '#6B7280'
                }))
                .sort((a: any, b: any) => a.stage_order - b.stage_order);
              setCrmStages(stages);
              stage = stages.find((s: any) => s.stage_order === stageOrder);
            }
          }
          
          if (stage) {
            setCurrentStageId(stage.id);
          } else {
            setCurrentStageId(null);
          }
        } else {
          setCurrentStageId(null);
        }
      }
    } catch (err: any) {
      console.error('Erro ao buscar estágio atual do lead:', err);
      setCurrentStageId(null);
    }
  };
  
  const handleStageChange = async (newStageId: string) => {
    if (!selectedContact || !newStageId || newStageId === currentStageId) {
      return;
    }

    setUpdatingStage(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/crm/contact/${selectedContact.id}/move-to-stage`, {
        stageId: newStageId
      });

      if (response.data.success) {
        setCurrentStageId(newStageId);
        // Atualizar o estágio na lista de conversas também
        const newStage = crmStages.find(s => s.id === newStageId);
        if (newStage) {
          setAllLeadsStages(prev => {
            const updated = new Map(prev);
            updated.set(selectedContact.id, newStage.stage_order);
            return updated;
          });
          
          // Atualizar stage4Leads se necessário
          if (newStage.stage_order === 4) {
            setStage4Leads(prev => new Set(prev).add(selectedContact.id));
          } else {
            setStage4Leads(prev => {
              const updated = new Set(prev);
              updated.delete(selectedContact.id);
              return updated;
            });
          }
        }
        console.log('✅ Estágio atualizado com sucesso!');
      } else {
        throw new Error(response.data.error || 'Erro ao atualizar estágio');
      }
    } catch (err: any) {
      console.error('❌ Erro ao atualizar estágio do lead:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao atualizar estágio do lead');
    } finally {
      setUpdatingStage(false);
    }
  };

  const handleCloseLeadDetails = () => {
    setOpenLeadDetailsDialog(false);
    setSelectedContact(null);
    setIsEditingName(false);
    setEditingName('');
  };

  const handleStartEditName = () => {
    setEditingName(selectedContact?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditingName(selectedContact?.name || '');
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!selectedContact || !editingName.trim() || editingName === selectedContact.name) {
      setIsEditingName(false);
      return;
    }

    setUpdatingName(true);
    try {
      console.log('💾 Salvando nome do lead:', {
        contactId: selectedContact.id,
        oldName: selectedContact.name,
        newName: editingName.trim(),
        url: `${API_BASE_URL}/crm/leads/${selectedContact.id}`
      });

      const response = await axios.put(`${API_BASE_URL}/crm/leads/${selectedContact.id}`, {
        name: editingName.trim()
      });

      console.log('✅ Resposta do servidor:', response.data);

      if (response.data.success) {
        // Atualizar o contato selecionado
        setSelectedContact({ ...selectedContact, name: editingName.trim() });
        
        // Atualizar na lista de conversas
        setConversations(prev => prev.map(conv => {
          if (conv.contact?.id === selectedContact.id) {
            return {
              ...conv,
              contact: { ...conv.contact, name: editingName.trim() }
            };
          }
          return conv;
        }));
        
        setIsEditingName(false);
        console.log('✅ Nome atualizado com sucesso!');
      } else {
        throw new Error(response.data.error || 'Erro ao atualizar nome');
      }
    } catch (err: any) {
      console.error('❌ Erro ao atualizar nome do lead:', err);
      console.error('❌ Detalhes do erro:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.error || err.message || 'Erro ao atualizar nome do lead');
      // Reverter para o nome original em caso de erro
      setEditingName(selectedContact.name || '');
    } finally {
      setUpdatingName(false);
    }
  };

  // Função para identificar região do Brasil pelo prefixo do telefone
  const getRegionFromPhone = (phone?: string): string | null => {
    if (!phone) return null;
    
    // Remover caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Verificar se começa com 55 (código do Brasil)
    if (cleanPhone.startsWith('55')) {
      // Pegar os 2 dígitos após o 55 (DDD)
      const ddd = cleanPhone.substring(2, 4);
      
      const regionMap: { [key: string]: { state: string; region: string } } = {
        '11': { state: 'São Paulo', region: 'Região Metropolitana de São Paulo/Região Metropolitana de Jundiaí/Região Geográfica Imediata de Bragança Paulista' },
        '12': { state: 'São Paulo', region: 'Região Metropolitana do Vale do Paraíba e Litoral Norte' },
        '13': { state: 'São Paulo', region: 'Região Metropolitana da Baixada Santista/Vale do Ribeira' },
        '14': { state: 'São Paulo', region: 'Avaré/Bauru/Botucatu/Jaú/Lins/Marília/Ourinhos' },
        '15': { state: 'São Paulo', region: 'Itapetininga/Itapeva/Sorocaba/Tatuí' },
        '16': { state: 'São Paulo', region: 'Araraquara/Franca/Jaboticabal/Ribeirão Preto/São Carlos/Sertãozinho' },
        '17': { state: 'São Paulo', region: 'Barretos/Catanduva/Fernandópolis/Jales/São José do Rio Preto/Votuporanga' },
        '18': { state: 'São Paulo', region: 'Andradina/Araçatuba/Assis/Birigui/Dracena/Presidente Prudente' },
        '19': { state: 'São Paulo', region: 'Americana/Campinas/Limeira/Piracicaba/Rio Claro/São João da Boa Vista' },
        '21': { state: 'Rio de Janeiro', region: 'Rio de Janeiro e Região Metropolitana/Teresópolis' },
        '22': { state: 'Rio de Janeiro', region: 'Cabo Frio/Campos dos Goytacazes/Itaperuna/Macaé/Nova Friburgo' },
        '24': { state: 'Rio de Janeiro', region: 'Angra dos Reis/Petrópolis/Volta Redonda/Piraí' },
        '27': { state: 'Espírito Santo', region: 'Vitória e Região Metropolitana/Colatina/Linhares/Santa Maria de Jetibá' },
        '28': { state: 'Espírito Santo', region: 'Cachoeiro de Itapemirim/Castelo/Itapemirim/Marataízes' },
        '31': { state: 'Minas Gerais', region: 'Belo Horizonte e Região Metropolitana/Conselheiro Lafaiete/Ipatinga/Viçosa' },
        '32': { state: 'Minas Gerais', region: 'Barbacena/Juiz de Fora/Muriaé/São João del-Rei/Ubá' },
        '33': { state: 'Minas Gerais', region: 'Almenara/Caratinga/Governador Valadares/Manhuaçu/Teófilo Otoni' },
        '34': { state: 'Minas Gerais', region: 'Araguari/Araxá/Patos de Minas/Uberlândia/Uberaba' },
        '35': { state: 'Minas Gerais', region: 'Alfenas/Guaxupé/Lavras/Poços de Caldas/Pouso Alegre/Varginha' },
        '37': { state: 'Minas Gerais', region: 'Bom Despacho/Divinópolis/Formiga/Itaúna/Pará de Minas' },
        '38': { state: 'Minas Gerais', region: 'Curvelo/Diamantina/Montes Claros/Pirapora/Unaí' },
        '41': { state: 'Paraná', region: 'Curitiba e Região Metropolitana' },
        '42': { state: 'Paraná', region: 'Ponta Grossa/Guarapuava' },
        '43': { state: 'Paraná', region: 'Apucarana/Londrina' },
        '44': { state: 'Paraná', region: 'Maringá/Campo Mourão/Umuarama' },
        '45': { state: 'Paraná', region: 'Cascavel/Foz do Iguaçu' },
        '46': { state: 'Paraná', region: 'Francisco Beltrão/Pato Branco' },
        '47': { state: 'Santa Catarina', region: 'Blumenau/Itajaí/Navegantes/Joinville/Brusque/Pomerode/Rio do Sul/Balneário Camboriú' },
        '48': { state: 'Santa Catarina', region: 'Florianópolis e Região Metropolitana/Nova Trento/São João Batista/Criciúma' },
        '49': { state: 'Santa Catarina', region: 'Caçador/Chapecó/Concórdia/Lages' },
        '51': { state: 'Rio Grande do Sul', region: 'Porto Alegre e Região Metropolitana/Santa Cruz do Sul/Litoral Norte' },
        '53': { state: 'Rio Grande do Sul', region: 'Pelotas/Rio Grande' },
        '54': { state: 'Rio Grande do Sul', region: 'Caxias do Sul/Passo Fundo' },
        '55': { state: 'Rio Grande do Sul', region: 'Santa Maria/Santana do Livramento/Santo Ângelo/Uruguaiana' },
        '61': { state: 'Distrito Federal/Goiás', region: 'Abrangência em todo o Distrito Federal e alguns municípios da Região Integrada de Desenvolvimento do Distrito Federal e Entorno' },
        '62': { state: 'Goiás', region: 'Goiânia e Região Metropolitana/Anápolis/Niquelândia/Porangatu' },
        '63': { state: 'Tocantins', region: 'Abrangência em todo o estado' },
        '64': { state: 'Goiás', region: 'Caldas Novas/Catalão/Itumbiara/Rio Verde' },
        '65': { state: 'Mato Grosso', region: 'Cuiabá e Região Metropolitana' },
        '66': { state: 'Mato Grosso', region: 'Rondonópolis/Sinop' },
        '67': { state: 'Mato Grosso do Sul', region: 'Abrangência em todo o estado' },
        '68': { state: 'Acre', region: 'Abrangência em todo o estado' },
        '69': { state: 'Rondônia', region: 'Abrangência em todo o estado' },
        '71': { state: 'Bahia', region: 'Salvador e Região Metropolitana' },
        '73': { state: 'Bahia', region: 'Eunápolis/Ilhéus/Porto Seguro/Teixeira de Freitas' },
        '74': { state: 'Bahia', region: 'Irecê/Jacobina/Juazeiro/Xique-Xique' },
        '75': { state: 'Bahia', region: 'Alagoinhas/Feira de Santana/Paulo Afonso/Valença' },
        '77': { state: 'Bahia', region: 'Barreiras/Bom Jesus da Lapa/Guanambi/Vitória da Conquista' },
        '79': { state: 'Sergipe', region: 'Abrangência em todo o estado' },
        '81': { state: 'Pernambuco', region: 'Recife e Região Metropolitana/Caruaru' },
        '82': { state: 'Alagoas', region: 'Abrangência em todo o estado' },
        '83': { state: 'Paraíba', region: 'Abrangência em todo o estado' },
        '84': { state: 'Rio Grande do Norte', region: 'Abrangência em todo o estado' },
        '85': { state: 'Ceará', region: 'Fortaleza e Região Metropolitana' },
        '86': { state: 'Piauí', region: 'Teresina e alguns municípios da Região Integrada de Desenvolvimento da Grande Teresina/Parnaíba' },
        '87': { state: 'Pernambuco', region: 'Garanhuns/Petrolina/Salgueiro/Serra Talhada' },
        '88': { state: 'Ceará', region: 'Juazeiro do Norte/Sobral' },
        '89': { state: 'Piauí', region: 'Picos/Floriano' },
        '91': { state: 'Pará', region: 'Belém e Região Metropolitana' },
        '92': { state: 'Amazonas', region: 'Manaus e Região Metropolitana/Parintins' },
        '93': { state: 'Pará', region: 'Santarém/Altamira/Itaituba' },
        '94': { state: 'Pará', region: 'Marabá' },
        '95': { state: 'Roraima', region: 'Abrangência em todo o estado' },
        '96': { state: 'Amapá', region: 'Abrangência em todo o estado' },
        '97': { state: 'Amazonas', region: 'Abrangência no interior do estado' },
        '98': { state: 'Maranhão', region: 'São Luís e Região Metropolitana' },
        '99': { state: 'Maranhão', region: 'Caxias/Codó/Imperatriz' },
      };
      
      const regionInfo = regionMap[ddd];
      if (regionInfo) {
        return `${regionInfo.state} - ${regionInfo.region}`;
      }
    } else if (cleanPhone.length >= 2) {
      // Se não começa com 55, pode ser que o DDD já esteja no início
      const ddd = cleanPhone.substring(0, 2);
      const regionMap: { [key: string]: { state: string; region: string } } = {
        '11': { state: 'São Paulo', region: 'Região Metropolitana de São Paulo/Região Metropolitana de Jundiaí/Região Geográfica Imediata de Bragança Paulista' },
        '12': { state: 'São Paulo', region: 'Região Metropolitana do Vale do Paraíba e Litoral Norte' },
        '13': { state: 'São Paulo', region: 'Região Metropolitana da Baixada Santista/Vale do Ribeira' },
        '14': { state: 'São Paulo', region: 'Avaré/Bauru/Botucatu/Jaú/Lins/Marília/Ourinhos' },
        '15': { state: 'São Paulo', region: 'Itapetininga/Itapeva/Sorocaba/Tatuí' },
        '16': { state: 'São Paulo', region: 'Araraquara/Franca/Jaboticabal/Ribeirão Preto/São Carlos/Sertãozinho' },
        '17': { state: 'São Paulo', region: 'Barretos/Catanduva/Fernandópolis/Jales/São José do Rio Preto/Votuporanga' },
        '18': { state: 'São Paulo', region: 'Andradina/Araçatuba/Assis/Birigui/Dracena/Presidente Prudente' },
        '19': { state: 'São Paulo', region: 'Americana/Campinas/Limeira/Piracicaba/Rio Claro/São João da Boa Vista' },
        '21': { state: 'Rio de Janeiro', region: 'Rio de Janeiro e Região Metropolitana/Teresópolis' },
        '22': { state: 'Rio de Janeiro', region: 'Cabo Frio/Campos dos Goytacazes/Itaperuna/Macaé/Nova Friburgo' },
        '24': { state: 'Rio de Janeiro', region: 'Angra dos Reis/Petrópolis/Volta Redonda/Piraí' },
        '27': { state: 'Espírito Santo', region: 'Vitória e Região Metropolitana/Colatina/Linhares/Santa Maria de Jetibá' },
        '28': { state: 'Espírito Santo', region: 'Cachoeiro de Itapemirim/Castelo/Itapemirim/Marataízes' },
        '31': { state: 'Minas Gerais', region: 'Belo Horizonte e Região Metropolitana/Conselheiro Lafaiete/Ipatinga/Viçosa' },
        '32': { state: 'Minas Gerais', region: 'Barbacena/Juiz de Fora/Muriaé/São João del-Rei/Ubá' },
        '33': { state: 'Minas Gerais', region: 'Almenara/Caratinga/Governador Valadares/Manhuaçu/Teófilo Otoni' },
        '34': { state: 'Minas Gerais', region: 'Araguari/Araxá/Patos de Minas/Uberlândia/Uberaba' },
        '35': { state: 'Minas Gerais', region: 'Alfenas/Guaxupé/Lavras/Poços de Caldas/Pouso Alegre/Varginha' },
        '37': { state: 'Minas Gerais', region: 'Bom Despacho/Divinópolis/Formiga/Itaúna/Pará de Minas' },
        '38': { state: 'Minas Gerais', region: 'Curvelo/Diamantina/Montes Claros/Pirapora/Unaí' },
        '41': { state: 'Paraná', region: 'Curitiba e Região Metropolitana' },
        '42': { state: 'Paraná', region: 'Ponta Grossa/Guarapuava' },
        '43': { state: 'Paraná', region: 'Apucarana/Londrina' },
        '44': { state: 'Paraná', region: 'Maringá/Campo Mourão/Umuarama' },
        '45': { state: 'Paraná', region: 'Cascavel/Foz do Iguaçu' },
        '46': { state: 'Paraná', region: 'Francisco Beltrão/Pato Branco' },
        '47': { state: 'Santa Catarina', region: 'Blumenau/Itajaí/Navegantes/Joinville/Brusque/Pomerode/Rio do Sul/Balneário Camboriú' },
        '48': { state: 'Santa Catarina', region: 'Florianópolis e Região Metropolitana/Nova Trento/São João Batista/Criciúma' },
        '49': { state: 'Santa Catarina', region: 'Caçador/Chapecó/Concórdia/Lages' },
        '51': { state: 'Rio Grande do Sul', region: 'Porto Alegre e Região Metropolitana/Santa Cruz do Sul/Litoral Norte' },
        '53': { state: 'Rio Grande do Sul', region: 'Pelotas/Rio Grande' },
        '54': { state: 'Rio Grande do Sul', region: 'Caxias do Sul/Passo Fundo' },
        '55': { state: 'Rio Grande do Sul', region: 'Santa Maria/Santana do Livramento/Santo Ângelo/Uruguaiana' },
        '61': { state: 'Distrito Federal/Goiás', region: 'Abrangência em todo o Distrito Federal e alguns municípios da Região Integrada de Desenvolvimento do Distrito Federal e Entorno' },
        '62': { state: 'Goiás', region: 'Goiânia e Região Metropolitana/Anápolis/Niquelândia/Porangatu' },
        '63': { state: 'Tocantins', region: 'Abrangência em todo o estado' },
        '64': { state: 'Goiás', region: 'Caldas Novas/Catalão/Itumbiara/Rio Verde' },
        '65': { state: 'Mato Grosso', region: 'Cuiabá e Região Metropolitana' },
        '66': { state: 'Mato Grosso', region: 'Rondonópolis/Sinop' },
        '67': { state: 'Mato Grosso do Sul', region: 'Abrangência em todo o estado' },
        '68': { state: 'Acre', region: 'Abrangência em todo o estado' },
        '69': { state: 'Rondônia', region: 'Abrangência em todo o estado' },
        '71': { state: 'Bahia', region: 'Salvador e Região Metropolitana' },
        '73': { state: 'Bahia', region: 'Eunápolis/Ilhéus/Porto Seguro/Teixeira de Freitas' },
        '74': { state: 'Bahia', region: 'Irecê/Jacobina/Juazeiro/Xique-Xique' },
        '75': { state: 'Bahia', region: 'Alagoinhas/Feira de Santana/Paulo Afonso/Valença' },
        '77': { state: 'Bahia', region: 'Barreiras/Bom Jesus da Lapa/Guanambi/Vitória da Conquista' },
        '79': { state: 'Sergipe', region: 'Abrangência em todo o estado' },
        '81': { state: 'Pernambuco', region: 'Recife e Região Metropolitana/Caruaru' },
        '82': { state: 'Alagoas', region: 'Abrangência em todo o estado' },
        '83': { state: 'Paraíba', region: 'Abrangência em todo o estado' },
        '84': { state: 'Rio Grande do Norte', region: 'Abrangência em todo o estado' },
        '85': { state: 'Ceará', region: 'Fortaleza e Região Metropolitana' },
        '86': { state: 'Piauí', region: 'Teresina e alguns municípios da Região Integrada de Desenvolvimento da Grande Teresina/Parnaíba' },
        '87': { state: 'Pernambuco', region: 'Garanhuns/Petrolina/Salgueiro/Serra Talhada' },
        '88': { state: 'Ceará', region: 'Juazeiro do Norte/Sobral' },
        '89': { state: 'Piauí', region: 'Picos/Floriano' },
        '91': { state: 'Pará', region: 'Belém e Região Metropolitana' },
        '92': { state: 'Amazonas', region: 'Manaus e Região Metropolitana/Parintins' },
        '93': { state: 'Pará', region: 'Santarém/Altamira/Itaituba' },
        '94': { state: 'Pará', region: 'Marabá' },
        '95': { state: 'Roraima', region: 'Abrangência em todo o estado' },
        '96': { state: 'Amapá', region: 'Abrangência em todo o estado' },
        '97': { state: 'Amazonas', region: 'Abrangência no interior do estado' },
        '98': { state: 'Maranhão', region: 'São Luís e Região Metropolitana' },
        '99': { state: 'Maranhão', region: 'Caxias/Codó/Imperatriz' },
      };
      
      const regionInfo = regionMap[ddd];
      if (regionInfo) {
        return `${regionInfo.state} - ${regionInfo.region}`;
      }
    }
    
    return null;
  };

  // Função para verificar se o lead está no estágio 4 (Venda Realizada)
  const isLeadInStage4 = async (contactId: string): Promise<boolean> => {
    try {
      console.log('🔍 Checking stage for contact:', contactId);
      // Usar a API /crm/all-leads que funciona corretamente
      const response = await axios.get(`${getApiUrl()}/crm/all-leads`);
      if (response.data.success && response.data.data) {
        const allLeads = response.data.data;
        console.log('📊 Total leads found:', allLeads.length);
        
        // Procurar o lead específico
        const targetLead = allLeads.find((lead: any) => lead.contacts?.id === contactId);
        
        if (targetLead) {
          console.log('🎯 Lead found in all-leads:', targetLead.contacts?.name);
          console.log('🎯 Stage order:', targetLead.crm_stages?.stage_order);
          const isStage4 = targetLead.crm_stages?.stage_order === 4;
          console.log('⭐ Is stage 4:', isStage4);
          return isStage4;
        } else {
          console.log('❌ Lead not found in all-leads');
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking lead stage:', error);
      return false;
    }
  };

  // Função para obter o estágio de qualquer lead
  const getLeadStage = (contactId: string): number | null => {
    return allLeadsStages.get(contactId) || null;
  };

  // Função para verificar estágios apenas dos contatos das conversas carregadas (otimizado)
  const checkAllLeadsStages = async (conversations: Conversation[]) => {
    console.log('🔍 checkAllLeadsStages called with conversations:', conversations.length);
    const stage4Set = new Set<string>();
    const stagesMap = new Map<string, number>();
    
    try {
      // Extrair apenas os contact_ids das conversas
      const contactIds = conversations
        .map(conv => conv.contact_id)
        .filter(Boolean) as string[];
      
      if (contactIds.length === 0) {
        console.log('⚠️ No contact IDs to check');
        setStage4Leads(stage4Set);
        setAllLeadsStages(stagesMap);
        return;
      }

      console.log(`📊 Buscando estágios para ${contactIds.length} contatos...`);
      
      // Buscar estágios apenas dos contatos das conversas carregadas (muito mais rápido)
      const response = await axios.post(`${getApiUrl()}/crm/contacts/stages`, {
        contact_ids: contactIds
      });
      
      if (response.data.success && response.data.data) {
        const stagesData = response.data.data; // { contact_id: stage_order }
        
        // Processar os estágios retornados
        Object.entries(stagesData).forEach(([contactId, stageOrder]) => {
          const stage = Number(stageOrder);
          if (stage) {
            stagesMap.set(contactId, stage);
            
            // Se for estágio 4, adicionar ao set
            if (stage === 4) {
              console.log('⭐ Adding to stage 4 set:', contactId);
              stage4Set.add(contactId);
            }
          }
        });
        
        console.log(`✅ Estágios encontrados para ${stagesMap.size} contatos`);
      }
    } catch (error) {
      console.error('Error fetching contact stages:', error);
    }
    
    console.log('🎯 Final stage 4 leads:', Array.from(stage4Set));
    console.log('🎯 All leads stages map:', Object.fromEntries(stagesMap));
    
    setStage4Leads(stage4Set);
    setAllLeadsStages(stagesMap);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showChatList, setShowChatList] = useState(true);

  if (loading) {
    return (
      <Box 
        sx={{
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100dvh',
          flexDirection: 'column',
          gap: 2.5,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%)',
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={48} sx={{ color: '#14B8A6' }} />
          <WhatsAppIcon sx={{ position: 'absolute', fontSize: 22, color: '#14B8A6' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a', mb: 0.5 }}>
            Carregando conversas
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem' }}>
            Aguarde enquanto conectamos ao WhatsApp...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Função para voltar à lista no mobile
  const handleBackToList = () => {
    setShowChatList(true);
    if (isMobile) {
      setSelectedConversation('');
    }
  };

  return (
    <>
    <Box sx={{ 
      height: { xs: '100%', md: 'calc(100vh - 80px)' },
      display: 'flex', flexDirection: 'column',
      bgcolor: '#f1f5f9',
      position: 'relative', overflow: 'hidden',
      p: { md: 2 },
    }}>
      {/* Mobile: Chat em tela cheia quando selecionado */}
      {isMobile && selectedConversation && !showChatList ? (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1300, bgcolor: '#ecfdf5',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Mobile header - extends behind status bar with gradient */}
          <Box sx={{
            display: 'flex', alignItems: 'center',
            px: 1.5, 
            pt: 'calc(env(safe-area-inset-top, 0px) + 10px)',
            pb: 1,
            background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            position: 'relative', zIndex: 1,
            flexShrink: 0,
          }}>
            <IconButton onClick={handleBackToList}
              sx={{ mr: 1, color: '#fff', p: 1 }}>
              <ArrowBackIcon sx={{ fontSize: 22 }} />
            </IconButton>
            <Avatar 
              src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
              sx={{ 
                mr: 1.5, width: 36, height: 36, fontSize: '0.8rem', fontWeight: 600,
                bgcolor: conversations.find(c => c.id === selectedConversation)?.contact?.name 
                  ? getAvatarColor(conversations.find(c => c.id === selectedConversation)?.contact?.name || '') 
                  : '#0d9488',
              }}
            >
              {conversations.find(c => c.id === selectedConversation)?.contact?.name 
                ? conversations.find(c => c.id === selectedConversation)?.contact?.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                : <WhatsAppIcon sx={{ fontSize: 18 }} />
              }
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: '0.92rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>
                WhatsApp
              </Typography>
              <LeadContactTags
                compact
                contactId={resolveConversationContactId(
                  conversations.find((c) => c.id === selectedConversation)
                )}
                onAssignmentsChanged={() => fetchConversations(true)}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {selectedConversation && (
                <Box sx={{ '& .MuiIconButton-root': { color: '#fff', p: '4px' }, '& .MuiChip-root': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                  <AttendantAssignment conversationId={selectedConversation} />
                </Box>
              )}
              <Box sx={{ '& .MuiIconButton-root': { color: '#fff', p: '4px' } }}>
                <BotToggleButton conversationId={selectedConversation} size="small" />
              </Box>
              <IconButton size="small"
                sx={{ color: '#fff' }}
                onClick={() => {
                  const currentContact = conversations.find(c => c.id === selectedConversation)?.contact;
                  if (currentContact) handleOpenLegacyDetails(currentContact);
                }}
                title="Ver detalhes do lead"
              >
                <PersonIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton size="small"
                sx={{ color: '#fff' }}
                onClick={() => {
                  const currentContact = conversations.find(c => c.id === selectedConversation)?.contact;
                  if (currentContact) handleContactClick(currentContact);
                }}
                title="Painel de Saúde"
              >
                <MedicalServicesIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {selectedConversation && (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 0.5,
                bgcolor: '#ffffff',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ChatSupportEventsBanner conversationId={selectedConversation} compact />
            </Box>
          )}

          {/* Área de mensagens mobile */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              flex: 1, px: 1.5, py: 2, overflow: 'auto',
              bgcolor: '#ecfdf5', // Verde mint suave para conforto visual
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23a7f3d0' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {messages.map((message) => (
                  <Box key={message.id}
                    sx={{ display: 'flex', justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start', mb: 0.5 }}
                  >
                    <Box
                      onClick={(e) => setMessageMenuAnchor({ el: e.currentTarget as HTMLElement, messageId: message.id })}
                      sx={{
                        maxWidth: '85%',
                        bgcolor: message.direction === 'outbound' ? '#d9fdd3' : '#ffffff',
                        color: '#111b21',
                        px: 1.5, py: 0.75,
                        borderRadius: message.direction === 'outbound' ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                        boxShadow: '0 1px 1px rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                      }}
                    >
                      {/* Renderizar anexos no mobile */}
                      {message.attachments && message.attachments.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          {message.attachments.map((attachment, index) => (
                            <Box key={index} sx={{ mb: 1 }}>
                              {attachment.type === 'image' && (
                                <Box sx={{ maxWidth: '100%', width: '100%' }}>
                                  <Box sx={{ 
                                    borderRadius: '8px 8px 0 0',
                                    overflow: 'hidden',
                                    maxWidth: '100%',
                                    width: '100%',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderBottom: 'none',
                                    display: 'block',
                                    cursor: 'pointer',
                                  }}
                                    onClick={() => setExpandedImage(normalizeAttachmentUrl(attachment.url))}
                                  >
                                    <img 
                                      src={normalizeAttachmentUrl(attachment.url)} 
                                      alt="Imagem"
                                      style={{ 
                                        width: '100%', 
                                        maxWidth: '100%',
                                        height: 'auto',
                                        maxHeight: 180,
                                        objectFit: 'contain',
                                        display: 'block',
                                      }}
                                      onError={(e) => {
                                        console.error('❌ Erro ao carregar imagem no mobile:', attachment.url);
                                        e.currentTarget.style.opacity = '0.5';
                                      }}
                                    />
                                  </Box>
                                  {/* Mobile action bar */}
                                  <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
                                    bgcolor: 'rgba(0,0,0,0.45)', borderRadius: '0 0 8px 8px',
                                    px: 0.75, py: 0.3,
                                  }}>
                                    <Tooltip title="Baixar imagem">
                                      <IconButton size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const link = document.createElement('a');
                                          link.href = normalizeAttachmentUrl(attachment.url);
                                          link.download = `image_${message.id}.jpg`;
                                          link.target = '_blank';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                        sx={{ color: 'rgba(255,255,255,0.8)', p: 0.4, '&:hover': { color: '#fff' } }}
                                      >
                                        <DownloadIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Salvar no banco de imagens do paciente">
                                      <IconButton size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAttachImageToGallery(normalizeAttachmentUrl(attachment.url));
                                        }}
                                        sx={{ color: '#5eead4', p: 0.4, '&:hover': { color: '#14B8A6' } }}
                                      >
                                        <MedicalServicesIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              )}
                              {attachment.type === 'video' && (
                                <Box sx={{ 
                                  borderRadius: 1, 
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  width: '100%',
                                  border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}>
                                  <video 
                                    controls
                                    preload="metadata"
                                    style={{ 
                                      width: '100%', 
                                      height: 'auto',
                                      maxHeight: 180,
                                      display: 'block'
                                    }}
                                  >
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/mp4" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/webm" />
                                    Seu navegador não suporta vídeos.
                                  </video>
                                </Box>
                              )}
                              {attachment.type === 'audio' && (
                                <Box sx={{ 
                                  borderRadius: 1, 
                                  p: 1,
                                  backgroundColor: 'rgba(0,0,0,0.05)'
                                }}>
                                  <audio controls style={{ width: '100%' }}>
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mp3" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                    Seu navegador não suporta áudio.
                                  </audio>
                                </Box>
                              )}
                              {attachment.type === 'voice' && (
                                <Box sx={{ 
                                  borderRadius: 1, 
                                  p: 1,
                                  backgroundColor: 'rgba(0,0,0,0.05)'
                                }}>
                                  <audio controls style={{ width: '100%' }}>
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                    Seu navegador não suporta áudio.
                                  </audio>
                                </Box>
                              )}
                              {attachment.type === 'document' && (
                                <Box sx={{ 
                                  borderRadius: 1, 
                                  p: 1,
                                  backgroundColor: 'rgba(0,0,0,0.05)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}>
                                  <Typography variant="body2">📎</Typography>
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    {attachment.file_name || 'Documento'}
                                  </Typography>
                                  <Tooltip title="Salvar nos documentos do paciente">
                                    <IconButton size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAttachDocToHealthcare(
                                          normalizeAttachmentUrl(attachment.url),
                                          attachment.file_name || 'documento_chat',
                                          attachment.mime_type
                                        );
                                      }}
                                      sx={{ color: '#14B8A6', p: 0.4, '&:hover': { color: '#0d9488' } }}
                                    >
                                      <MedicalServicesIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => window.open(normalizeAttachmentUrl(attachment.url), '_blank')}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                      
                      {/* Mobile message text */}
                      {message.content && (() => {
                        const hasImageAttachment = message.attachments && message.attachments.some((att: any) => att.type === 'image');
                        const shouldHideGenericText = hasImageAttachment && (
                          message.content === 'Mensagem image' || message.content === 'Imagem' ||
                          message.content.toLowerCase().includes('image') || !message.content.trim()
                        );
                        if (shouldHideGenericText) return null;
                        return (
                          <Typography sx={{
                            fontSize: '0.9375rem', lineHeight: 1.45,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            wordBreak: 'break-word', whiteSpace: 'pre-wrap', color: '#111b21',
                          }}>
                            {message.content}
                          </Typography>
                        );
                      })()}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Typography sx={{ fontSize: '0.6875rem', color: '#667781' }}>
                          {formatTime(message.sent_at)}
                        </Typography>
                        {message.direction === 'outbound' && (
                          <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                            <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.142.47.47 0 0 0-.338.142.345.345 0 0 0-.107.254c0 .091.04.18.107.254l2.726 2.576a.506.506 0 0 0 .344.142c.142 0 .27-.066.37-.186l6.514-8.034a.35.35 0 0 0 .095-.256.37.37 0 0 0-.095-.19z" fill="#53bdeb"/>
                            <path d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.134a.145.145 0 0 0-.016.142l.838.792a.506.506 0 0 0 .344.142c.142 0 .27-.066.37-.186l6.514-8.034a.35.35 0 0 0 .095-.256.37.37 0 0 0-.07-.178z" fill="#53bdeb"/>
                          </svg>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
              </Box>
            </Box>

            {/* Mobile input */}
            <Box sx={{ px: 1.5, pt: 1, bgcolor: '#f0f2f5', borderTop: '1px solid #e2e8f0', pb: 'max(8px, env(safe-area-inset-bottom, 8px))', flexShrink: 0 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,text/plain,.txt,application/zip,.zip"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {replyingToMessage && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  mb: 1, p: 1, bgcolor: '#fff', borderRadius: '10px',
                  borderLeft: '4px solid #14B8A6',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#14B8A6', fontWeight: 600, fontSize: '0.72rem', display: 'block' }}>
                      {replyingToMessage.direction === 'outbound' ? 'Você' : conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Contato'}
                    </Typography>
                    <Typography sx={{ color: '#667781', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                      {replyingToMessage.content}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setReplyingToMessage(null)} sx={{ p: 0.25, color: '#94a3b8' }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}
              
              {/* Mobile file preview */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  {selectedFiles.length === 1 ? (
                    <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                      {selectedFiles[0].type.startsWith('image/') && (
                        <Box component="img" src={URL.createObjectURL(selectedFiles[0])} alt=""
                          sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                      )}
                      {selectedFiles[0].type.startsWith('video/') && (
                        <Box sx={{ width: 48, height: 48, bgcolor: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography>🎥</Typography>
                        </Box>
                      )}
                      {(selectedFiles[0].type.startsWith('audio/') || selectedFiles[0].name.includes('recording')) && (
                        <Box sx={{ width: 48, height: 48, bgcolor: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography>🎤</Typography>
                        </Box>
                      )}
                      {!selectedFiles[0].type.startsWith('image/') && !selectedFiles[0].type.startsWith('video/') && !selectedFiles[0].type.startsWith('audio/') && !selectedFiles[0].name.includes('recording') && (
                        <Box sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'rgba(0, 0, 0, 0.1)',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Typography variant="h6">📎</Typography>
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {selectedFiles[0].name}
                        </Typography>
                        {selectedFiles[0].size > 0 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {(selectedFiles[0].size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => handleCancelFile(0)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#0f172a' }}>
                        📎 {selectedFiles.length} arquivos
                      </Typography>
                      <IconButton size="small" onClick={() => handleCancelFile()} sx={{ color: '#94a3b8' }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Mobile recording indicator */}
              {isRecording && (
                <Box sx={{ mb: 1, p: 1, bgcolor: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                  <Box sx={{
                    width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444',
                    animation: 'recPulse 1.4s ease-in-out infinite',
                    '@keyframes recPulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.4, transform: 'scale(0.8)' } },
                  }} />
                  <Typography sx={{ flex: 1, color: '#ef4444', fontWeight: 600, fontSize: '0.82rem' }}>
                    Gravando {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </Typography>
                  <IconButton size="small" onClick={handleStopRecording} sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                    <StopIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                <IconButton size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage || isRecording}
                  sx={{ color: '#54656f', '&:hover': { color: '#14B8A6' } }}
                >
                  <AttachFileIcon sx={{ fontSize: 22 }} />
                </IconButton>
                
                <TextField fullWidth multiline maxRows={4}
                  placeholder={selectedFiles.length > 0 ? "Legenda..." : "Mensagem"}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey && !isRecording) { e.preventDefault(); handleSendMessage(); } }}
                  disabled={sendingMessage || isRecording}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px', bgcolor: '#fff', minHeight: 40,
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&.Mui-focused fieldset': { borderColor: '#14B8A6', borderWidth: '1.5px' },
                      '& input, & textarea': { fontSize: '0.9rem', color: '#111b21', py: 0.75, px: 1.25 },
                    },
                  }}
                />
                
                {newMessage.trim() || selectedFiles.length > 0 ? (
                  <IconButton onClick={handleSendMessage} disabled={sendingMessage}
                    sx={{ bgcolor: '#14B8A6', color: '#fff', width: 40, height: 40, borderRadius: '10px',
                      '&:hover': { bgcolor: '#0d9488' }, '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}
                  >
                    {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={isRecording ? handleStopRecording : handleStartRecording}
                    sx={{ bgcolor: isRecording ? '#ef4444' : '#f1f5f9', color: isRecording ? '#fff' : '#54656f',
                      width: 40, height: 40, borderRadius: '10px', '&:hover': { bgcolor: isRecording ? '#dc2626' : '#e2e8f0' } }}
                  >
                    {isRecording ? <StopIcon sx={{ fontSize: 20 }} /> : <MicIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Desktop: Layout normal */
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 0, md: 2 }, 
          height: '100%',
          flex: 1,
          overflow: 'hidden',
        }}>
          {/* Lista de Conversas */}
          <Paper elevation={0} sx={{ 
            flex: { xs: showChatList ? '1' : '0', md: '0 0 380px' },
            minWidth: { xs: 'auto', md: 380 },
            display: { xs: showChatList ? 'flex' : 'none', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 2 : 1, md: 1 },
            position: { xs: 'relative', md: 'static' },
            overflow: 'hidden',
            borderRadius: '16px', 
            flexDirection: 'column',
            minHeight: 0,
            bgcolor: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
          {/* Sidebar header */}
          <Box sx={{ 
            px: { xs: 1.5, md: 2.5 }, 
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 12px)', md: 2 },
            pb: { xs: 1.5, md: 2 },
            background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMobile && outletContext?.toggleDrawer && (
                <IconButton size="small" onClick={outletContext.toggleDrawer}
                  sx={{ color: '#fff', mr: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                  <MenuIcon sx={{ fontSize: 22 }} />
                </IconButton>
              )}
              <Box sx={{
                width: 36, height: 36, borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <WhatsAppIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
              <Box>
                <Typography sx={{ 
                  color: '#ffffff', fontWeight: 700, fontSize: '1rem',
                  lineHeight: 1.2, letterSpacing: '-0.01em',
                }}>
                  Conversas
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.72rem', fontWeight: 500 }}>
                  {conversations.filter(c => c.status !== 'archived').length} ativas
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Atualizar conversas">
              <IconButton size="small" onClick={() => fetchConversations(true)}
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        
        {error && (
          <Box px={2} pt={1.5}>
            <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.8rem', py: 0.5 }}>{error}</Alert>
          </Box>
        )}
        
        {/* Search + archive filter */}
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar conversa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: '#94a3b8', mr: 1, fontSize: 19 }} />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#f8fafc',
                height: 40,
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#f1f5f9' },
                '&.Mui-focused': {
                  bgcolor: '#fff',
                  boxShadow: '0 0 0 2px rgba(20,184,166,0.15)',
                  '& fieldset': { borderColor: '#14B8A6' },
                },
                '& fieldset': { borderColor: '#e2e8f0' },
                '& input': {
                  fontSize: '0.85rem', color: '#334155',
                  '&::placeholder': { color: '#94a3b8', opacity: 1 },
                },
              },
            }}
          />
          {/* Archive toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Chip
              icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
              label={showArchived ? 'Mostrando arquivadas' : 'Arquivadas ocultas'}
              size="small"
              variant={showArchived ? 'filled' : 'outlined'}
              onClick={() => setShowArchived(!showArchived)}
              sx={{
                height: 26, fontSize: '0.72rem', fontWeight: 500,
                borderRadius: '8px',
                ...(showArchived
                  ? { bgcolor: 'rgba(20,184,166,0.1)', color: '#0f766e', border: '1px solid rgba(20,184,166,0.3)' }
                  : { borderColor: '#e2e8f0', color: '#94a3b8' }),
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Box>
        </Box>

        <Box px={2} pb={1.5}>
          <InboxConversationTagFilter
            selectedTagIds={inboxFilterTagIds}
            onChange={setInboxFilterTagIds}
          />
        </Box>
        
        <List sx={{ 
          py: 0.5, 
          flex: 1, 
          overflow: 'auto',
          minHeight: 0,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1', borderRadius: '4px',
            '&:hover': { background: '#94a3b8' },
          },
        }}>
           {(() => {
             let filteredConversations = searchTerm.trim() === '' 
               ? conversations 
               : conversations.filter((conv) => {
                   const searchLower = searchTerm.toLowerCase();
                   const name = conv.contact?.name?.toLowerCase() || '';
                   const phone = conv.contact?.phone?.toLowerCase() || '';
                   const email = conv.contact?.email?.toLowerCase() || '';
                   return name.includes(searchLower) || phone.includes(searchLower) || email.includes(searchLower);
                 });
             
             if (!showArchived) {
               filteredConversations = filteredConversations.filter(conv => conv.status !== 'archived');
             }
             
             if (filteredConversations.length === 0) {
               return (
                 <Box sx={{ p: 4, textAlign: 'center' }}>
                   <SearchIcon sx={{ fontSize: 36, color: '#e2e8f0', mb: 1 }} />
                   <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                     {searchTerm.trim() ? 'Nenhum resultado encontrado' : 'Nenhuma conversa'}
                   </Typography>
                 </Box>
               );
             }
             
             return filteredConversations.map((conversation) => {
              const isSelected = selectedConversation === conversation.id;
              const hasUnread = conversation.unread_count > 0;
              return (
              <ListItem
                key={conversation.id}
                component="div"
                onClick={() => {
                  setSelectedConversation(conversation.id);
                  if (isMobile) setShowChatList(false);
                }}
                sx={{
                  cursor: 'pointer',
                  px: 2, py: 1.25,
                  mx: 0.75, my: 0.25,
                  borderRadius: '12px',
                  bgcolor: isSelected ? 'rgba(20,184,166,0.08)' : 'transparent',
                  borderLeft: isSelected ? '3px solid #14B8A6' : '3px solid transparent',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  '&:hover': {
                    bgcolor: isSelected ? 'rgba(20,184,166,0.12)' : '#f8fafc',
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 52 }}>
                  <Avatar 
                    src={conversation.contact?.avatar_url}
                    sx={{ 
                      bgcolor: conversation.contact?.name ? getAvatarColor(conversation.contact.name) : '#14B8A6',
                      width: 44, height: 44,
                      fontSize: '0.9rem', fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  >
                    {conversation.contact?.name 
                      ? conversation.contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : <PersonIcon sx={{ fontSize: 20 }} />
                    }
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{ m: 0, overflow: 'hidden' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                        <Typography sx={{ 
                          fontWeight: hasUnread ? 700 : (isSelected ? 600 : 500),
                          fontSize: '0.88rem', color: '#0f172a',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {conversation.contact?.name || 'Sem nome'}
                        </Typography>
                        {conversation.contact?.id && stage4Leads.has(conversation.contact.id) && (
                          <StarIcon sx={{ fontSize: 14, color: '#f59e0b', flexShrink: 0 }} />
                        )}
                      </Box>
                      <Typography sx={{ 
                        fontSize: '0.7rem', color: hasUnread ? '#14B8A6' : '#94a3b8',
                        fontWeight: hasUnread ? 600 : 400, flexShrink: 0, whiteSpace: 'nowrap',
                      }}>
                        {formatTime(conversation.last_message_at || conversation.updated_at || '')}
                      </Typography>
                      <ConversationListContactTags tags={conversation.contact_tags} />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                        {conversation.status === 'archived' && (
                          <Chip label="Arq." size="small" sx={{
                            height: 18, fontSize: '0.6rem', fontWeight: 600,
                            bgcolor: '#f1f5f9', color: '#94a3b8', borderRadius: '4px',
                          }} />
                        )}
                        <Typography sx={{
                          fontSize: '0.78rem', color: hasUnread ? '#475569' : '#94a3b8',
                          fontWeight: hasUnread ? 500 : 400,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {conversation.last_message 
                            ? conversation.last_message.substring(0, 50)
                            : 'Sem mensagens'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <AttendantAssignment conversationId={conversation.id} compact={true} />
                        {/* Archive button - only on hover */}
                        <IconButton size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (conversation.status === 'archived') {
                              handleUnarchiveConversation(conversation.id);
                            } else {
                              handleArchiveConversation(conversation.id);
                            }
                          }}
                          sx={{
                            opacity: 0, p: 0.3, color: '#94a3b8',
                            '.MuiListItem-root:hover &': { opacity: 1 },
                            '&:hover': { color: '#14B8A6', bgcolor: 'rgba(20,184,166,0.08)' },
                          }}
                        >
                          {conversation.status === 'archived' 
                            ? <UnarchiveIcon sx={{ fontSize: 16 }} />
                            : <ArchiveIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                        {hasUnread && (
                          <Box sx={{
                            bgcolor: '#14B8A6', color: '#fff',
                            borderRadius: '10px', minWidth: 20, height: 20,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.65rem', fontWeight: 700, px: 0.5,
                          }}>
                            {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
              );
             });
           })()}
          {hasMoreConversations && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Button
                fullWidth
                variant="text"
                onClick={loadMoreConversations}
                disabled={loadingMoreConversations}
                startIcon={loadingMoreConversations ? <CircularProgress size={16} sx={{ color: '#14B8A6' }} /> : null}
                sx={{
                  borderRadius: '10px', textTransform: 'none', py: 1,
                  color: '#14B8A6', fontWeight: 600, fontSize: '0.8rem',
                  bgcolor: 'rgba(20,184,166,0.04)',
                  '&:hover': { bgcolor: 'rgba(20,184,166,0.08)' },
                }}
              >
                {loadingMoreConversations ? 'Carregando...' : 'Carregar mais'}
              </Button>
            </Box>
          )}
        </List>
      </Paper>

          {/* Área de Mensagens */}
          <Box sx={{ 
            flex: { xs: showChatList ? '0' : '1', md: 1 },
            minWidth: 0,
            display: { xs: showChatList ? 'none' : 'flex', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 1 : 2, md: 1 },
            position: { xs: 'relative', md: 'static' },
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
        {selectedConversation ? (
          <>
            {/* Cabeçalho da Conversa */}
            <Box sx={{ 
              px: 2.5, py: 1.5,
              bgcolor: '#ffffff',
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
                  sx={{ 
                    bgcolor: (() => {
                      const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                      return contact?.name ? getAvatarColor(contact.name) : '#14B8A6';
                    })(),
                    width: 46, height: 46, flexShrink: 0,
                    fontSize: '1rem', fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '& img': { objectFit: 'cover', width: '100%', height: '100%' },
                  }}
                >
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    return contact?.name 
                      ? contact.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : <PersonIcon />;
                  })()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
                    </Typography>
                    {(() => {
                      const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                      return contact && stage4Leads.has(contact.id) ? (
                        <StarIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                      ) : null;
                    })()}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
                    <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                      WhatsApp
                    </Typography>
                  </Box>
                </Box>
                {/* Action toolbar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttendantAssignment conversationId={selectedConversation} />
                  <BotToggleButton conversationId={selectedConversation} size="small" />
                  <Tooltip title="Ver detalhes do lead" arrow>
                    <IconButton size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) handleOpenLegacyDetails(contact);
                      }}
                      sx={{ color: '#64748b', '&:hover': { bgcolor: 'rgba(20,184,166,0.08)', color: '#14B8A6' } }}
                    >
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ width: '1px', height: 24, bgcolor: '#e2e8f0', mx: 0.5 }} />
                  <Tooltip title="Painel de Saúde" arrow>
                    <IconButton size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) handleContactClick(contact);
                      }}
                      sx={{ color: '#14B8A6', '&:hover': { bgcolor: 'rgba(20,184,166,0.08)' } }}
                    >
                      <MedicalServicesIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Atualizar foto" arrow>
                    <IconButton size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) updateContactAvatar(contact.id);
                      }}
                      disabled={updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                      sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                    >
                      {updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id 
                        ? <CircularProgress size={18} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir lead" arrow>
                    <IconButton size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) deleteContact(contact.id);
                      }}
                      disabled={deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                      sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#fef2f2', color: '#ef4444' } }}
                    >
                      {deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id 
                        ? <CircularProgress size={18} /> : <DeleteIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <LeadContactTags
                contactId={resolveConversationContactId(
                  conversations.find((c) => c.id === selectedConversation)
                )}
                onAssignmentsChanged={() => fetchConversations(true)}
              />
            </Box>

            {selectedConversation && (
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2.5,
                  py: 0.5,
                  bgcolor: '#ffffff',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <ChatSupportEventsBanner conversationId={selectedConversation} compact />
              </Box>
            )}

            {/* Área de Mensagens */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: '500px',
              bgcolor: '#ecfdf5', // Verde mint suave para conforto visual
              position: 'relative',
              // Subtle WhatsApp-style pattern com toque verde
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23a7f3d0' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}>
              <Box 
                ref={messagesScrollContainerRef}
                sx={{ 
                flex: 1, overflow: 'auto', position: 'relative', zIndex: 1,
                minHeight: 0, px: { xs: 1.5, md: 3 }, py: 2,
                '&::-webkit-scrollbar': { width: '5px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' },
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {messages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                    mb: 0.5,
                  }}
                >
                  <Paper
                    elevation={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMessageMenuAnchor({ el: e.currentTarget, messageId: message.id });
                    }}
                    sx={{
                      px: 1.75, py: 1,
                      maxWidth: { xs: '85%', md: '65%' },
                      bgcolor: message.direction === 'outbound' ? '#d9fdd3' : '#ffffff',
                      color: '#111b21',
                      borderRadius: message.direction === 'outbound' 
                        ? '10px 10px 2px 10px' 
                        : '10px 10px 10px 2px',
                      boxShadow: '0 1px 1px rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        '& .msg-menu-hint': { opacity: 1 },
                      },
                    }}
                  >
                    {/* Renderizar anexos */}
                    {(() => {
                      // Debug: verificar se há attachments
                      if (message.attachments && message.attachments.length > 0) {
                        console.log('📎 Mensagem com attachments:', message.id, message.attachments);
                      }
                      
                      // Verificar se há attachments ou se o conteúdo parece ser um arquivo
                      const hasAttachments = message.attachments && message.attachments.length > 0;
                      const contentLooksLikeFile = message.content && (
                        message.content.match(/\.(jpg|jpeg|png|gif|webp|mp4|webm|mp3|ogg|wav|pdf|doc|docx)$/i) ||
                        message.content.includes('recording-') ||
                        message.content.includes('IMG_') ||
                        message.content.includes('VID_')
                      );
                      
                      if (!hasAttachments && contentLooksLikeFile) {
                        console.log('⚠️ Mensagem parece ter arquivo mas sem attachments:', message.id, message.content);
                      }
                      
                      return hasAttachments;
                    })() && message.attachments && (
                      <Box sx={{ mb: 1 }}>
                        {message.attachments.map((attachment, index) => {
                          console.log('📎 Renderizando attachment:', attachment.type, attachment.url);
                          return (
                          <Box key={index} sx={{ mb: 1 }}>
                            {attachment.type === 'image' && (
                              <Box sx={{ maxWidth: '100%', width: '100%' }}>
                                {/* Imagem */}
                                <Box sx={{ 
                                  borderRadius: '8px 8px 0 0', 
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  width: '100%',
                                  position: 'relative',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  borderBottom: 'none',
                                  display: 'block',
                                  cursor: 'pointer',
                                }}
                                  onClick={() => setExpandedImage(normalizeAttachmentUrl(attachment.url))}
                                >
                                  <img 
                                    src={normalizeAttachmentUrl(attachment.url)} 
                                    alt="Imagem"
                                    style={{ 
                                      width: '100%', 
                                      maxWidth: '100%',
                                      height: 'auto',
                                      maxHeight: isMobile ? 180 : 220,
                                      objectFit: 'contain',
                                      display: 'block',
                                      visibility: 'visible',
                                      opacity: 1
                                    }}
                                    onError={(e) => {
                                      console.error('❌ Erro ao carregar imagem:', attachment.url, e);
                                      e.currentTarget.style.opacity = '0.5';
                                      e.currentTarget.alt = 'Erro ao carregar imagem';
                                    }}
                                    onLoad={() => {
                                      console.log('✅ Imagem carregada com sucesso:', attachment.url);
                                    }}
                                  />
                                  {/* Zoom hint */}
                                  <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 5,
                                    bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', borderRadius: '6px', p: 0.4, pointerEvents: 'none',
                                    opacity: 0, transition: 'opacity 0.2s', '.MuiPaper-root:hover &': { opacity: 1 } }}>
                                    <ZoomInIcon sx={{ fontSize: 16 }} />
                                  </Box>
                                </Box>
                                {/* Action bar */}
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                  bgcolor: 'rgba(0,0,0,0.45)', borderRadius: '0 0 8px 8px',
                                  px: 0.75, py: 0.35,
                                }}>
                                  {(attachment.width || attachment.height) && (
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', flex: 1 }}>
                                      {attachment.width && attachment.height && `${attachment.width}×${attachment.height}`}
                                    </Typography>
                                  )}
                                  <Box sx={{ flex: 1 }} />
                                  <Tooltip title="Baixar imagem">
                                    <IconButton size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = normalizeAttachmentUrl(attachment.url);
                                        link.download = `image_${message.id}.jpg`;
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                    >
                                      <DownloadIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Salvar no banco de imagens do paciente">
                                    <IconButton size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAttachImageToGallery(normalizeAttachmentUrl(attachment.url));
                                      }}
                                      sx={{
                                        color: '#5eead4', p: 0.5,
                                        '&:hover': { color: '#14B8A6', bgcolor: 'rgba(20,184,166,0.15)' },
                                      }}
                                    >
                                      <MedicalServicesIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'video' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                maxWidth: '100%',
                                width: '100%',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                display: 'block'
                              }}>
                                <Box sx={{ position: 'relative', width: '100%' }}>
                                  <video 
                                    controls
                                    preload="metadata"
                                    style={{ 
                                      width: '100%', 
                                      height: 'auto',
                                      maxHeight: isMobile ? 180 : 250,
                                      display: 'block'
                                    }}
                                  >
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/mp4" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/webm" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/ogg" />
                                    Seu navegador não suporta vídeos.
                                  </video>
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8,
                                    zIndex: 10
                                  }}>
                                    <IconButton 
                                      size="small"
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = normalizeAttachmentUrl(attachment.url);
                                        link.download = `video_${message.id}.mp4`;
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      sx={{ 
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' }
                                      }}
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                {attachment.duration && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, px: 1, color: 'text.secondary' }}>
                                    Duração: {Math.floor(attachment.duration / 60)}:{(attachment.duration % 60).toString().padStart(2, '0')}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {attachment.type === 'audio' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mp3" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/wav" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  {attachment.title && (
                                    <Typography variant="caption" sx={{ flex: 1 }}>
                                      {attachment.title}
                                      {attachment.performer && ` - ${attachment.performer}`}
                                    </Typography>
                                  )}
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = attachment.title 
                                        ? `${attachment.title}.mp3` 
                                        : `audio_${message.id}.mp3`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'voice' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/webm" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ flex: 1 }}>
                                    🎤 Mensagem de voz
                                  </Typography>
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = `voice_${message.id}.ogg`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'document' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Typography variant="body2">📎</Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {attachment.file_name || 'Documento'}
                                </Typography>
                                <Tooltip title="Salvar nos documentos do paciente">
                                  <IconButton size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAttachDocToHealthcare(
                                        normalizeAttachmentUrl(attachment.url),
                                        attachment.file_name || 'documento_chat',
                                        attachment.mime_type
                                      );
                                    }}
                                    sx={{ color: '#14B8A6', '&:hover': { color: '#0d9488' } }}
                                  >
                                    <MedicalServicesIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton 
                                  size="small" 
                                  onClick={() => window.open(normalizeAttachmentUrl(attachment.url), '_blank')}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Box>
                            )}
                            {attachment.type === 'sticker' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                display: 'inline-block'
                              }}>
                                <img 
                                  src={normalizeAttachmentUrl(attachment.url)} 
                                  alt="Sticker"
                                  style={{ 
                                    width: 120, 
                                    height: 120,
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        );
                        })}
                      </Box>
                    )}
                    
                    {/* Renderizar texto da mensagem */}
                    {message.content && (() => {
                      const hasImageAttachment = message.attachments && message.attachments.some((att: any) => att.type === 'image');
                      const shouldHideGenericText = hasImageAttachment && (
                        message.content === 'Mensagem image' || 
                        message.content === 'Imagem' ||
                        message.content.toLowerCase().includes('image') ||
                        !message.content.trim()
                      );
                      
                      if (shouldHideGenericText) return null;
                      
                      return (
                        <Typography sx={{
                          fontSize: '0.9375rem', lineHeight: 1.45,
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          fontWeight: 400, letterSpacing: 'normal',
                          wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                          color: '#111b21',
                        }}>
                          {message.content}
                        </Typography>
                      );
                    })()}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Typography sx={{ 
                        fontSize: '0.6875rem', color: '#667781',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      }}>
                        {formatTime(message.sent_at)}
                      </Typography>
                      {message.direction === 'outbound' && (
                        <Box sx={{ display: 'flex', ml: 0.25 }}>
                          <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                            <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.142.47.47 0 0 0-.338.142.345.345 0 0 0-.107.254c0 .091.04.18.107.254l2.726 2.576a.506.506 0 0 0 .344.142c.142 0 .27-.066.37-.186l6.514-8.034a.35.35 0 0 0 .095-.256.37.37 0 0 0-.095-.19z" fill="#53bdeb"/>
                            <path d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.134a.145.145 0 0 0-.016.142l.838.792a.506.506 0 0 0 .344.142c.142 0 .27-.066.37-.186l6.514-8.034a.35.35 0 0 0 .095-.256.37.37 0 0 0-.07-.178z" fill="#53bdeb"/>
                          </svg>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                  <Menu
                    anchorEl={messageMenuAnchor?.messageId === message.id ? messageMenuAnchor.el : null}
                    open={messageMenuAnchor?.messageId === message.id || false}
                    onClose={() => {
                      setMessageMenuAnchor(null);
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setReplyingToMessage(message);
                        setMessageMenuAnchor(null);
                      }}
                    >
                      <EditIcon fontSize="small" sx={{ mr: 1 }} />
                      Responder
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleDeleteMessage(message);
                      }}
                      disabled={deletingMessageId === message.id}
                    >
                      {deletingMessageId === message.id ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                      )}
                      Deletar mensagem
                    </MenuItem>
                  </Menu>
                </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
                </Box>
              </Box>
            </Box>

            {/* Campo de Envio */}
            <Box sx={{ 
              px: 2, py: 1.5,
              bgcolor: '#f0f2f5',
              borderTop: '1px solid #e2e8f0',
              flexShrink: 0,
            }}>
              {/* Reply preview */}
              {replyingToMessage && (
                <Box sx={{ 
                  mb: 1, mx: 0.5, p: 1.25,
                  bgcolor: '#fff', borderRadius: '10px',
                  borderLeft: '4px solid #14B8A6',
                  display: 'flex', alignItems: 'flex-start', gap: 1,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: '#14B8A6', fontWeight: 600, fontSize: '0.75rem', mb: 0.25, lineHeight: 1.2 }}>
                      {replyingToMessage.direction === 'inbound' ? conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Contato' : 'Você'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#667781', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {replyingToMessage.content || '📎 Mídia'}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setReplyingToMessage(null)} sx={{ p: 0.25, color: '#94a3b8' }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}

              {/* File preview */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 1, mx: 0.5 }}>
                  {selectedFiles.length === 1 ? (
                    <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                      {selectedFiles[0].type.startsWith('image/') && (
                        <Box component="img" src={URL.createObjectURL(selectedFiles[0])} alt=""
                          sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedFiles[0].name}
                        </Typography>
                        {selectedFiles[0].size > 0 && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {(selectedFiles[0].size / 1024 / 1024).toFixed(1)} MB
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => handleCancelFile(0)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#0f172a' }}>
                        📎 {selectedFiles.length} arquivos selecionados
                      </Typography>
                      <IconButton size="small" onClick={() => handleCancelFile()} sx={{ color: '#94a3b8' }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}

              {/* Recording indicator */}
              {isRecording && (
                <Box sx={{ mb: 1, mx: 0.5, p: 1, bgcolor: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ 
                    width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444',
                    animation: 'recPulse 1.4s ease-in-out infinite',
                    '@keyframes recPulse': { '0%, 100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.4, transform: 'scale(0.8)' } },
                  }} />
                  <Typography sx={{ flex: 1, color: '#ef4444', fontWeight: 600, fontSize: '0.82rem' }}>
                    Gravando {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </Typography>
                  <IconButton size="small" onClick={handleStopRecording} sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                    <StopIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              )}

              {/* Input row */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <input ref={fileInputRef} type="file"
                  accept="image/*,video/*,audio/*,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,text/plain,.txt,application/zip,.zip"
                  multiple style={{ display: 'none' }} onChange={handleFileSelect}
                />

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                  <Tooltip title="Anexar arquivo" arrow>
                    <IconButton size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sendingMessage || isRecording}
                      sx={{ color: '#54656f', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', color: '#14B8A6' } }}
                    >
                      <AttachFileIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                  {!isRecording && !selectedFiles.length && (
                    <Tooltip title="Gravar áudio" arrow>
                      <IconButton size="small"
                        onClick={handleStartRecording}
                        disabled={sendingMessage}
                        sx={{ color: '#54656f', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', color: '#14B8A6' } }}
                      >
                        <MicIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {selectedConversation && (
                    <QuickMessagesButton
                      onSelectMessage={(msg) => { setNewMessage(msg); messageInputRef.current?.focus(); }}
                      channel="whatsapp"
                    />
                  )}
                </Box>

                {/* Text input */}
                <TextField
                  fullWidth variant="outlined"
                  placeholder={selectedFiles.length > 0 ? "Legenda (opcional)..." : "Digite uma mensagem"}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isRecording) {
                      e.preventDefault(); handleSendMessage();
                    }
                  }}
                  onPaste={handlePasteImage}
                  inputRef={messageInputRef}
                  disabled={sendingMessage || isRecording}
                  multiline maxRows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#ffffff', borderRadius: '10px',
                      minHeight: 42,
                      '& fieldset': { borderColor: '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#cbd5e1' },
                      '&.Mui-focused fieldset': { borderColor: '#14B8A6', borderWidth: '1.5px' },
                      '& input, & textarea': {
                        fontSize: '0.9rem', color: '#111b21', py: 1, px: 1.5,
                        '&::placeholder': { color: '#8696a0', opacity: 1 },
                      },
                    },
                  }}
                />

                {/* Send button */}
                <IconButton
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || isRecording}
                  sx={{ 
                    flexShrink: 0, width: 42, height: 42,
                    bgcolor: '#14B8A6', color: '#fff', borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(20,184,166,0.3)',
                    '&:hover': { bgcolor: '#0d9488' },
                    '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
                    transition: 'all 0.15s ease',
                  }}
                >
                  {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: '#f8fafc', borderRadius: '16px',
            border: '1px solid #e2e8f0',
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '20px',
              bgcolor: 'rgba(20,184,166,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mb: 3,
            }}>
              <WhatsAppIcon sx={{ fontSize: 36, color: '#14B8A6' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', mb: 1 }}>
              Pooh Saúde Inbox
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.9rem', textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
              Selecione uma conversa na lista ao lado para visualizar e responder mensagens
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {conversations.filter(c => c.status !== 'archived').length} conversas ativas
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
          </Box>
        </Box>
      )}
      </Box>

      {/* Dialog para Imagem Ampliada */}
      <Dialog 
        open={!!expandedImage} 
        onClose={() => setExpandedImage(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 2,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          color: '#fff',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Imagem Ampliada
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => {
                if (expandedImage) {
                  const link = document.createElement('a');
                  link.href = normalizeAttachmentUrl(expandedImage);
                  link.download = `image_${Date.now()}.jpg`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={() => setExpandedImage(null)}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            overflow: 'auto'
          }}
        >
          {expandedImage && (
            <img
              src={normalizeAttachmentUrl(expandedImage)}
              alt="Imagem ampliada"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para Detalhes do Lead */}
      <Dialog open={openLeadDetailsDialog} onClose={handleCloseLeadDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={selectedContact?.avatar_url || undefined}
              sx={{
                bgcolor: selectedContact?.avatar_url ? 'transparent' : '#25D366' + '15',
                color: '#25D366',
                fontWeight: 600,
                width: 48,
                height: 48,
                fontSize: '1.2rem'
              }}
            >
              {!selectedContact?.avatar_url && (selectedContact?.name || 'Lead').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              {isEditingName ? (
                <TextField
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  variant="standard"
                  fullWidth
                  autoFocus
                  disabled={updatingName}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      fontSize: '1.25rem', 
                      fontWeight: 600,
                      py: 0.5
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      handleCancelEditName();
                    }
                  }}
                />
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedContact?.name || 'Lead'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366' }} />
                <Typography variant="body2" color="text.secondary">
                  WhatsApp Lead
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {isEditingName ? (
                <>
                  <IconButton
                    size="small"
                    onClick={handleSaveName}
                    disabled={updatingName || !editingName.trim() || editingName.trim() === selectedContact?.name}
                    color="primary"
                  >
                    {updatingName ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancelEditName}
                    disabled={updatingName}
                  >
                    <CancelIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  size="small"
                  onClick={handleStartEditName}
                  title="Editar nome"
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Informações Básicas */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Informações Básicas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Nome
                  </Typography>
                  {isEditingName ? (
                    <TextField
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                      disabled={updatingName}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveName();
                        } else if (e.key === 'Escape') {
                          handleCancelEditName();
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                        {selectedContact?.name || 'Não informado'}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={handleStartEditName}
                        title="Editar nome"
                        sx={{ ml: 'auto' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.email || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.phone || 'Não informado'}
                  </Typography>
                  {selectedContact?.phone && getRegionFromPhone(selectedContact.phone) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                      📍 {getRegionFromPhone(selectedContact.phone)}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Canal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      WhatsApp
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* IDs do WhatsApp */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                IDs do WhatsApp
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    WhatsApp ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.custom_fields?.whatsapp_id || 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ID Interno
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.id || 'Não disponível'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Estágio do CRM */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Estágio do CRM
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="stage-select-label">Estágio</InputLabel>
                <Select
                  labelId="stage-select-label"
                  value={currentStageId || ''}
                  label="Estágio"
                  onChange={(e) => handleStageChange(e.target.value)}
                  disabled={updatingStage || crmStages.length === 0}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                >
                  {crmStages.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: stage.color,
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="body2">
                          {stage.stage_order}. {stage.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {updatingStage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Atualizando estágio...
                    </Typography>
                  </Box>
                )}
              </FormControl>
            </Box>

            {/* Campos Customizados */}
            {selectedContact?.custom_fields && Object.keys(selectedContact.custom_fields).length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  Campos Customizados
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {selectedContact && selectedContact.custom_fields && Object.entries(selectedContact.custom_fields).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {String(value) || 'Não disponível'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {isEditingName ? (
            <>
              <Button 
                onClick={handleCancelEditName}
                disabled={updatingName}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveName}
                disabled={updatingName || !editingName.trim() || editingName.trim() === selectedContact?.name}
                variant="contained"
                color="primary"
                startIcon={updatingName ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {updatingName ? 'Salvando...' : 'Salvar'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseLeadDetails}>Fechar</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Painel de Saúde - Healthcare Panel */}
      <HealthcarePanel
        open={openHealthcarePanel}
        contactId={selectedContact?.id || null}
        contactName={selectedContact?.name}
        contactPhone={selectedContact?.phone}
        contactEmail={selectedContact?.email}
        onSendMessage={(message) => {
          setNewMessage(message);
          setOpenHealthcarePanel(false);
        }}
        onSendMediaUrl={(url, type, fileName) => {
          handleSendMediaFromHealthcare(url, type, fileName);
          setOpenHealthcarePanel(false);
        }}
        onClose={() => {
          setOpenHealthcarePanel(false);
        }}
      />

      {/* Feedback: salvar imagem na galeria */}
      {(attachingToGallery || attachGalleryMsg) && (
        <Box sx={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, minWidth: 280, maxWidth: 400,
        }}>
          <Alert
            severity={attachingToGallery ? 'info' : ((attachGalleryMsg.includes('sucesso') || attachGalleryMsg.includes('salv')) ? 'success' : 'error')}
            onClose={attachingToGallery ? undefined : () => setAttachGalleryMsg('')}
            sx={{ boxShadow: 4, borderRadius: 2 }}
          >
            {attachingToGallery ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Salvando no prontuário do paciente...
              </Box>
            ) : attachGalleryMsg}
          </Alert>
        </Box>
      )}
    </>
  );
};

export default WhatsApp;